<template>
  <!-- FI-1818 tenant admin for the ops desk surface. one composite GET on
       mount hydrates all five sections; per-tab save calls hit granular
       PATCH endpoints so a slug-uniqueness error doesn't roll back the
       distribution edit on the next tab. hosted exclusively inside the ops events
       list's settings modal (the /ops_desk_settings route redirects there),
       so the modal chrome owns the title + close. -->
  <div class="od-set-shell">
    <div v-if="loading" class="od-set-loading">
      <i class="fa fa-spinner fa-spin"></i>&nbsp;Loading settings...
    </div>

    <template v-else>
      <fi-tab-bar
        v-model="activeTab"
        :tabs="tabDefs"
        id-prefix="od-set-tab"
        aria-label="Event Response Manager settings sections"
      />
      <div class="od-set-tabs-content">

      <!-- ===== Public form ===== -->
      <section v-show="activeTab === 'form'" role="tabpanel" aria-labelledby="od-set-tab-form">
        <div class="od-set-section">
          <p class="od-set-help">
            The form path is the end of your form's URL. Use lowercase
            letters, digits, and hyphens, 3 to 40 characters, starting
            with a letter.
          </p>

          <label class="od-set-label">Form path</label>
          <div class="od-set-slug-row">
            <b-form-input
              v-model="form.slug"
              placeholder="e.g. acme-fleet"
              :state="slugInputState"
              class="od-set-slug-input"
            />
            <!-- at rest the row shows a single quiet "Saved" chip in the
                 button slot — no Save button, no in-input check, no status
                 line (see slugInputState / slugStatusText). the moment the
                 admin edits, the chip flips to an actionable Save and the
                 validation affordances light up. -->
            <b-button
              v-if="slugIsDraft || !savedSlug"
              variant="primary" size="sm"
              :disabled="savingSlug || !slugCanSave"
              @click="saveSlug"
              class="od-set-slug-save-btn"
            >
              <span v-if="savingSlug">Saving...</span><span v-else>Save</span>
            </b-button>
            <span v-else class="od-set-slug-saved-chip" :title="slugSavedAt ? ('Last saved ' + formatRelative(slugSavedAt)) : 'Current form path'">
              <i class="fa fa-check"></i>&nbsp;Saved
            </span>
          </div>
          <!-- status line + error only surface while editing a draft; the
               resting "owned_by_self" verdict is intentionally silent so the
               chip is the lone confirmation. -->
          <div class="od-set-slug-status" :class="slugStatusClass" v-if="slugStatusText">
            <i class="fa" :class="slugStatusIcon"></i>&nbsp;{{ slugStatusText }}
          </div>
          <div v-if="slugError" class="od-set-error">{{ slugError }}</div>

          <label class="od-set-label od-set-label--small">Form URL</label>
          <div class="od-set-url-box">
            <!-- the url renders as one inline-block span per path piece so a
                 narrow box wraps after a slash instead of mid-word -->
            <code v-if="publicUrlPreview"><span v-for="(seg, i) in publicUrlSegments" :key="i" class="od-set-url-seg">{{ seg }}</span></code>
            <code v-else>(set a form path first)</code>
            <div class="od-set-url-actions" v-if="publicUrlPreview">
              <b-button size="sm" variant="outline-secondary" class="od-set-url-btn" @click="copyPublicUrl">
                <i class="fa" :class="copiedAt ? 'fa-check' : 'fa-clipboard'"></i>&nbsp;<span v-if="copiedAt">Copied</span><span v-else>Copy</span>
              </b-button>
              <b-button size="sm" variant="outline-secondary" class="od-set-url-btn" :href="publicUrlPreview" target="_blank" rel="noopener">
                <i class="fa fa-external-link"></i>&nbsp;Open
              </b-button>
            </div>
          </div>
          <div class="od-set-url-hint" v-if="slugIsDraft && publicUrlPreview">
            <i class="fa fa-info-circle"></i>&nbsp;This link goes live when you save.
          </div>

          <!-- access toggle: when off, the public form returns a friendly
               "unavailable" state to non-authed visitors. authed staff
               still file in-app via "+ Add entry" without using the path. -->
          <label class="od-set-label od-set-label--small">Public access</label>
          <div class="od-set-access-row">
            <od-toggle
              v-model="form.public_form_enabled"
              :disabled="savingAccess"
              class="od-set-access-toggle"
              :aria-label="form.public_form_enabled ? 'Public access is on' : 'Public access is off'"
            />
            <div class="od-set-access-text">
              <div class="od-set-access-headline">
                <span v-if="form.public_form_enabled">Anyone with the link can submit</span>
                <span v-else>Logged-in users only</span>
              </div>
              <div class="od-set-access-sub">
                <span v-if="form.public_form_enabled">
                  Visitors don't need an account. Share the link above by email,
                  on posters, or as a QR code.
                </span>
                <span v-else>
                  The link still works, but anyone who isn't signed in sees a
                  "form unavailable" message. Your team can still add entries
                  from inside Event Response Manager.
                </span>
              </div>
            </div>
            <span v-if="accessSavedAt" class="od-set-saved-hint">Saved {{ formatRelative(accessSavedAt) }}</span>
          </div>
          <div v-if="accessError" class="od-set-save-error">{{ accessError }}</div>
        </div>
      </section>

      <!-- ===== Notifications (internally: distribution) ===== -->
      <section v-show="activeTab === 'distribution'" role="tabpanel" aria-labelledby="od-set-tab-distribution">
        <div class="od-set-section">
          <p class="od-set-help">
            Who gets notified for every event submission and comment.
            Portal users get an in-app notification and an email; outside
            addresses get the email only. Departments deliver to whoever
            is a member when the notification goes out, so roster changes
            apply automatically.
          </p>

          <label class="od-set-label">Portal users</label>
          <!-- restrict only when we actually have a roster to pick from.
               get_users is soft-caught to [] on failure, so an
               unconditional restrict would silently block every entry if
               that call ever fails. fall back to free entry in that case. -->
          <od-tag-input
            v-model="form.fi_user_ids"
            separator=" ,;"
            :placeholder="tenantUsersLoading ? 'Loading users...' : 'Start typing a name or username...'"
            variant="primary"
            class="od-set-tags"
            :suggestions="fiUserSuggestions"
            :restrict-to-suggestions="tenantUsersLoaded && tenantUserCandidates.length > 0"
            invalid-text="Pick a user from the list"
          />
          <div class="od-set-hint" v-if="tenantUsersLoaded && !tenantUserCandidates.length">
            No portal users to pick from yet.
          </div>

          <label class="od-set-label">Departments</label>
          <!-- closed set: only departments defined on the Departments tab
               make sense here, and names resolve to members at send time. -->
          <od-tag-input
            v-model="form.distribution_departments"
            separator=","
            :placeholder="form.departments.length ? 'Add a department...' : 'No departments defined yet'"
            variant="info"
            class="od-set-tags"
            :suggestions="departmentSuggestions"
            :restrict-to-suggestions="true"
            invalid-text="Pick a department from the list"
            duplicate-text="Already in list"
          />
          <div class="od-set-hint" v-if="!form.departments.length">
            Define departments on the Departments tab to route by team.
          </div>

          <label class="od-set-label">Outside email addresses</label>
          <od-tag-input
            v-model="form.email_only"
            separator=" ,;"
            placeholder="Add email and press Enter..."
            variant="secondary"
            class="od-set-tags"
            :validator="emailValidator"
            :suggestions="emailOnlySuggestions"
            invalid-text="Not a valid email address"
            duplicate-text="Already in list"
          />

          <div class="od-set-actions">
            <div class="od-set-actions-primary">
              <span v-if="distributionDirty" class="od-set-unsaved-hint">Unsaved changes</span>
              <span v-else-if="distributionSavedAt" class="od-set-saved-hint">Saved {{ formatRelative(distributionSavedAt) }}</span>
              <b-button variant="primary" size="sm" :disabled="savingDistribution || !distributionDirty" @click="saveDistribution">
                <i class="fa fa-save"></i>&nbsp;<span v-if="savingDistribution">Saving...</span><span v-else>Save</span>
              </b-button>
            </div>
          </div>
          <div v-if="distributionError" class="od-set-save-error">{{ distributionError }}</div>
        </div>
      </section>

      <!-- ===== Categories ===== -->
      <section v-show="activeTab === 'categories'" role="tabpanel" aria-labelledby="od-set-tab-categories">
        <div class="od-set-section">
          <p class="od-set-help">
            Choose which categories appear in the form and the in-app picker.
            All of them show by default; save your selection to limit it.
          </p>

          <div class="od-set-cats-grid">
            <div class="od-set-cats-col">
              <div class="od-set-cats-header">
                <span>Issue categories</span>
                <span class="od-set-cats-bulk">
                  <button type="button" class="od-set-cats-bulk-btn" @click="selectAllCategories('incident')">All</button>
                  <span class="od-set-cats-bulk-sep">/</span>
                  <button type="button" class="od-set-cats-bulk-btn" @click="clearCategories('incident')">None</button>
                </span>
              </div>
              <div v-for="cat in incidentCategories" :key="'i-' + cat" class="od-set-cat-row">
                <b-form-checkbox v-model="form.enabled_incident" :value="cat" class="od-set-cat-check">
                  {{ readableLabel(cat) }}<span
                    v-if="categoryUsage[cat]"
                    class="od-set-cat-count"
                    :title="usageTitle(cat)"
                  >{{ categoryUsage[cat] }}</span>
                </b-form-checkbox>
              </div>
            </div>
            <div class="od-set-cats-col">
              <div class="od-set-cats-header">
                <span>Kudos categories</span>
                <span class="od-set-cats-bulk">
                  <button type="button" class="od-set-cats-bulk-btn" @click="selectAllCategories('kudos')">All</button>
                  <span class="od-set-cats-bulk-sep">/</span>
                  <button type="button" class="od-set-cats-bulk-btn" @click="clearCategories('kudos')">None</button>
                </span>
              </div>
              <div v-for="cat in kudosCategories" :key="'k-' + cat" class="od-set-cat-row">
                <b-form-checkbox v-model="form.enabled_kudos" :value="cat" class="od-set-cat-check">
                  {{ readableLabel(cat) }}<span
                    v-if="categoryUsage[cat]"
                    class="od-set-cat-count"
                    :title="usageTitle(cat)"
                  >{{ categoryUsage[cat] }}</span>
                </b-form-checkbox>
              </div>
            </div>
          </div>

          <!-- an empty incident set still saves (the public form hides the
               category picker and submits without one), but warn so the
               admin knows what the form will look like. -->
          <div v-if="!form.enabled_incident.length" class="od-set-cats-warning">
            <i class="fa fa-info-circle"></i>&nbsp;With no issue categories enabled, the public form and the event picker stop offering categories. Pick at least one if you want reporters to classify issues.
          </div>

          <div class="od-set-actions">
            <b-button variant="link" size="sm" class="od-set-action-secondary" @click="restoreDefaultCategories">
              <i class="fa fa-undo"></i>&nbsp;Restore defaults
            </b-button>
            <div class="od-set-actions-primary">
              <span v-if="categoriesDirty" class="od-set-unsaved-hint">Unsaved changes</span>
              <span v-else-if="categoriesSavedAt" class="od-set-saved-hint">Saved {{ formatRelative(categoriesSavedAt) }}</span>
              <b-button variant="primary" size="sm" :disabled="savingCategories || !categoriesDirty" @click="saveCategories">
                <i class="fa fa-save"></i>&nbsp;<span v-if="savingCategories">Saving...</span><span v-else>Save</span>
              </b-button>
            </div>
          </div>
          <div v-if="categoriesError" class="od-set-save-error">{{ categoriesError }}</div>
        </div>
      </section>

      <!-- ===== Departments ===== -->
      <section v-show="activeTab === 'departments'" role="tabpanel" aria-labelledby="od-set-tab-departments">
        <div class="od-set-section">
          <p class="od-set-help">
            Departments are the teams that respond to events, like Maintenance,
            Safety, or Dispatch. Add a department and choose who's on it.
          </p>

          <div v-if="form.departments.length" class="od-dept-list">
            <div v-for="(dept, i) in form.departments" :key="'dept-' + i" class="od-dept-card">
              <div class="od-dept-card-head">
                <b-form-input
                  v-model="dept.name"
                  size="sm"
                  class="od-dept-name-input"
                  placeholder="Department name"
                />
                <button
                  type="button"
                  class="od-dept-remove"
                  :aria-label="'Remove ' + (dept.name || 'department')"
                  title="Remove department"
                  @click="removeDepartment(i)"
                >
                  <i class="fa fa-trash-o"></i>
                </button>
              </div>
              <div v-if="departmentsBlankAttempted && !(dept.name || '').trim()" class="od-dept-name-error">
                Name this department or remove it.
              </div>
              <label class="od-set-label od-set-label--small">Members</label>
              <od-tag-input
                v-model="dept.members"
                separator=" ,;"
                :placeholder="tenantUsersLoading ? 'Loading users...' : 'Add a member by name...'"
                variant="info"
                :suggestions="fiUserSuggestions"
                :restrict-to-suggestions="tenantUsersLoaded && tenantUserCandidates.length > 0"
                invalid-text="Pick a user from the list"
              />
              <div class="od-dept-member-empty" v-if="!dept.members.length">
                No members yet. Add the people who handle this team's work.
              </div>
            </div>
          </div>
          <div v-else class="od-dept-empty">
            No departments yet. Add one below to get started.
          </div>

          <template v-if="availableDeptSuggestions.length">
            <label class="od-set-label od-set-label--small">Quick add</label>
            <div class="od-dept-suggest-row">
              <button
                v-for="d in availableDeptSuggestions"
                :key="'sug-' + d"
                type="button"
                class="od-dept-chip od-dept-chip--suggest"
                @click="addDepartment(d)"
              >
                <i class="fa fa-plus"></i>&nbsp;{{ d }}
              </button>
            </div>
          </template>

          <label class="od-set-label od-set-label--small">Add a department</label>
          <div class="od-dept-custom-row">
            <b-form-input
              v-model="deptDraft"
              placeholder="e.g. Charter Sales"
              size="sm"
              class="od-dept-custom-input"
              @keydown.enter.prevent="commitDeptDraft"
            />
            <b-button size="sm" variant="outline-secondary" :disabled="!deptDraft.trim()" @click="commitDeptDraft">
              Add
            </b-button>
          </div>

          <div class="od-set-actions">
            <div class="od-set-actions-primary">
              <span v-if="departmentsDirty" class="od-set-unsaved-hint">Unsaved changes</span>
              <span v-else-if="departmentsSavedAt" class="od-set-saved-hint">Saved {{ formatRelative(departmentsSavedAt) }}</span>
              <b-button variant="primary" size="sm" :disabled="savingDepartments || !departmentsDirty" @click="saveDepartments">
                <i class="fa fa-save"></i>&nbsp;<span v-if="savingDepartments">Saving...</span><span v-else>Save</span>
              </b-button>
            </div>
          </div>
          <div v-if="departmentsError" class="od-set-save-error">{{ departmentsError }}</div>
        </div>
      </section>

      <!-- ===== Huddle ===== -->
      <section v-show="activeTab === 'huddle'" role="tabpanel" aria-labelledby="od-set-tab-huddle">
        <div class="od-set-section">
          <p class="od-set-help">
            A recap of recent event activity, emailed to your distribution
            list on the schedule you set. Pick the days and time that work for
            your team, and manage who receives it on the Notifications tab.
          </p>

          <!-- section heads give the tab room to grow: upcoming huddle
               expansion (insights, stats) slots in as new sections. -->
          <h4 class="od-set-sect-head">Delivery</h4>
          <div class="od-set-access-row od-set-access-row--compact">
            <od-toggle
              v-model="form.huddle_enabled"
              class="od-set-access-toggle"
              :aria-label="form.huddle_enabled ? 'Scheduled sends are on' : 'Scheduled sends are off'"
            />
            <div class="od-set-access-text">
              <div class="od-set-access-headline">
                <span v-if="form.huddle_enabled">Runs on the schedule below</span>
                <span v-else>No scheduled sends</span>
              </div>
              <div class="od-set-access-sub">
                <span v-if="form.huddle_enabled">
                  Subscribers receive the recap automatically. Contact support if you
                  need it sent outside the schedule.
                </span>
                <span v-else>
                  Subscriptions stay intact; nothing sends until you turn this on.
                  Contact support if you need a recap sent in the meantime.
                </span>
              </div>
            </div>
          </div>

          <label class="od-set-label">Send on</label>
          <div class="od-huddle-day-chips" role="group" aria-label="Days to send the huddle">
            <button v-for="d in huddleDayOptions" :key="d.value"
                    type="button"
                    :class="['od-huddle-day-chip', { 'od-huddle-day-chip--active': huddleDays.includes(d.value) }]"
                    @click="toggleHuddleDay(d.value)">
              {{ d.label }}
            </button>
          </div>

          <div class="od-huddle-when-row">
            <div class="od-huddle-when-col">
              <label class="od-set-label">At</label>
              <div class="od-huddle-time-picker">
                <b-form-select v-model="huddleHour12" :options="huddleHourOptions" size="sm" class="od-huddle-time-sel"/>
                <span class="od-huddle-time-colon">:</span>
                <b-form-select v-model="huddleMinute" :options="huddleMinuteOptions" size="sm" class="od-huddle-time-sel"/>
                <b-form-select v-model="huddleAmPm" :options="huddleAmPmOptions" size="sm" class="od-huddle-time-sel od-huddle-time-sel--ampm"/>
              </div>
            </div>
            <div class="od-huddle-when-col">
              <label class="od-set-label">Timezone</label>
              <b-form-select v-model="form.huddle_tz" :options="tzOptions" size="sm" class="od-set-select"/>
            </div>
          </div>

          <label class="od-set-label od-set-label--small">Preview</label>
          <div class="od-set-preview-box">
            <i class="fa fa-calendar-o"></i>&nbsp;{{ huddleScheduleLabel || 'Pick at least one day' }}
          </div>

          <div v-if="huddleCronUnparseable" class="od-huddle-advanced-warning">
            <i class="fa fa-info-circle"></i>
            Your existing schedule uses an advanced expression
            (<code>{{ form.huddle_cron }}</code>) that this picker can't represent.
            Saving will replace it with the simple schedule above.
          </div>

          <div class="od-set-actions">
            <div class="od-set-actions-primary">
              <span v-if="huddleDirty" class="od-set-unsaved-hint">Unsaved changes</span>
              <span v-else-if="huddleSavedAt" class="od-set-saved-hint">Saved {{ formatRelative(huddleSavedAt) }}</span>
              <b-button variant="primary" size="sm" :disabled="savingHuddle || !huddleDays.length || !huddleDirty" @click="saveHuddle">
                <i class="fa fa-save"></i>&nbsp;<span v-if="savingHuddle">Saving...</span><span v-else>Save</span>
              </b-button>
            </div>
          </div>
          <div v-if="huddleError" class="od-set-save-error">{{ huddleError }}</div>

          <!-- 2026-07-31 review: the today board was the only opt-in surface
               and customers never see it, so enrollment lives here. changes
               apply immediately (no save step), like the toggles below.
               these rows are ADDITIONS: the sender unions them with the
               notifications distribution list, so this list never shows
               (or silences) people the distribution carries. -->
          <h4 class="od-set-sect-head">Additional recipients</h4>
          <p class="od-set-help">The huddle goes to your notification distribution list (see the Notifications tab). Anyone added here receives it as well.</p>
          <div class="od-set-sub-list">
            <div v-for="s in huddleSubscribers" :key="s.user_id" class="od-set-sub-row">
              <span class="od-set-sub-id">{{ s.email || s.user_id }}</span>
              <span v-if="s.email && s.user_id !== s.email" class="od-set-sub-user">{{ s.user_id }}</span>
              <button type="button" class="od-set-sub-remove" :disabled="huddleSubBusy"
                      :title="'Remove ' + (s.email || s.user_id)"
                      :aria-label="'Remove ' + (s.email || s.user_id)"
                      @click="removeHuddleSubscriber(s)">
                <i class="fa fa-times" aria-hidden="true"></i>
              </button>
            </div>
            <div v-if="!huddleSubscribers.length" class="od-set-sub-empty">No additional recipients.</div>
          </div>
          <div class="od-set-sub-add">
            <b-form-input v-model="huddleSubAdd" size="sm" placeholder="user@example.com"
                          class="od-set-sub-input" @keyup.enter="addHuddleSubscriber"/>
            <b-button size="sm" variant="outline-secondary" :disabled="huddleSubBusy || !huddleSubAdd.trim()" @click="addHuddleSubscriber">
              <i class="fa fa-plus"></i>&nbsp;Add
            </b-button>
          </div>
          <div v-if="huddleSubError" class="od-set-save-error">{{ huddleSubError }}</div>

          <!-- self-contained toggle: saves on flip (see saveFaBrief), so it
               lives outside the schedule section's Save. -->
          <h4 class="od-set-sect-head">Fleet Assistant Brief</h4>
          <div class="od-set-access-row od-set-access-row--compact">
            <od-toggle
              v-model="form.fa_brief_enabled"
              :disabled="!faEnabled || savingFaBrief"
              class="od-set-access-toggle"
              :aria-label="form.fa_brief_enabled ? 'Fleet Assistant Brief is on' : 'Fleet Assistant Brief is off'"
            />
            <div class="od-set-access-text">
              <div class="od-set-access-headline">
                <span v-if="!faEnabled">Requires Fleet Assistant</span>
                <span v-else-if="form.fa_brief_enabled">Daily brief is on</span>
                <span v-else>Daily brief is off</span>
              </div>
              <div class="od-set-access-sub">
                <span v-if="!faEnabled">Turn on Fleet Assistant for this tenant to enable the Brief.</span>
                <span v-else-if="form.fa_brief_enabled">Fleet Assistant writes a short daily summary of your open events and refreshes it as they change. Generation counts toward your monthly AI budget.</span>
                <span v-else>Turn this on for a short daily summary of your open events, written by Fleet Assistant.</span>
              </div>
            </div>
            <span v-if="faBriefSavedAt" class="od-set-saved-hint">Saved {{ formatRelative(faBriefSavedAt) }}</span>
          </div>
          <div v-if="faBriefError" class="od-set-save-error">{{ faBriefError }}</div>
        </div>
      </section>

      <!-- ===== Import ===== -->
      <section v-show="activeTab === 'import'" role="tabpanel" aria-labelledby="od-set-tab-import">
        <div class="od-set-section">

          <!-- connect wizard: no sheet saved yet, or the admin is changing sheets -->
          <template v-if="importWizardActive">
            <p class="od-set-help">
              Keep your existing Google Sheet and Event Response Manager in step. Connect the
              sheet below; Fleet Integrations reads it on a schedule and mirrors each
              row into Event Response Manager. It's one-way and read-only, so nothing about your
              sheet changes and entries your team works in Event Response Manager are kept.
            </p>

            <div class="od-imp-step">
              <div class="od-imp-step-head"><span class="od-imp-step-num">1</span> Share your sheet</div>
              <div class="od-imp-step-body">
                <div class="od-set-preview-box od-imp-sa">
                  <span class="od-imp-sa-email">{{ importServiceAccount || 'unavailable' }}</span>
                  <b-button variant="link" size="sm" class="od-imp-copy" :disabled="!importServiceAccount"
                            :title="importSaCopied ? 'Copied' : 'Copy address'" @click="copyServiceAccount">
                    <i :class="importSaCopied ? 'fa fa-check' : 'fa fa-clipboard'"></i>
                  </b-button>
                </div>
                <p class="od-set-help od-imp-help-sub">
                  Add this address as a Viewer in the sheet's Share dialog. It's a
                  service account, not a person, so no one is emailed.
                </p>
              </div>
            </div>

            <div class="od-imp-step">
              <div class="od-imp-step-head"><span class="od-imp-step-num">2</span> Paste the sheet link</div>
              <div class="od-imp-step-body">
                <div class="od-imp-connect-row">
                  <b-form-input v-model="form.import_spreadsheet_id" size="sm" class="od-set-input"
                                placeholder="https://docs.google.com/spreadsheets/d/…"
                                @input="resetImportInspect"
                                @keydown.enter.prevent="inspectImport"/>
                  <b-button variant="primary" size="sm"
                            :disabled="inspectingImport || !(form.import_spreadsheet_id || '').trim()"
                            @click="inspectImport">
                    <span v-if="inspectingImport">Connecting...</span><span v-else>Connect</span>
                  </b-button>
                </div>
                <div v-if="importInspectOk" class="od-set-preview-box od-imp-test-ok">
                  <i class="fa fa-check-circle"></i>&nbsp;Found <strong>{{ importInspect.title || 'your sheet' }}</strong>.
                </div>
                <div v-else-if="importInspect" class="od-set-save-error">{{ importInspect.message }}</div>
              </div>
            </div>

            <div v-if="importInspectOk" class="od-imp-step">
              <div class="od-imp-step-head"><span class="od-imp-step-num">3</span> Choose the tab to import</div>
              <div class="od-imp-step-body">
                <div class="od-imp-tab-list" role="radiogroup" aria-label="Sheet tabs">
                  <label v-for="t in importInspect.tabs" :key="t.title" class="od-imp-tab-opt"
                         :class="{ 'od-imp-tab-opt--active': form.import_tab === t.title }">
                    <input type="radio" name="od-imp-tab" class="od-imp-tab-radio"
                           :value="t.title" :checked="form.import_tab === t.title"
                           @change="pickImportTab(t.title)"/>
                    <i class="fa fa-table" aria-hidden="true"></i>
                    <span class="od-imp-tab-name">{{ t.title }}</span>
                  </label>
                </div>
                <div v-if="testingImport" class="od-set-help od-imp-help-sub"><i class="fa fa-circle-o-notch fa-spin"></i>&nbsp;Checking the tab...</div>
                <div v-else-if="importTestResult && importTestResult.ok" class="od-set-preview-box od-imp-test-ok">
                  <i class="fa fa-check-circle"></i>&nbsp;{{ importTestResult.message }}
                </div>
                <div v-else-if="importTestResult" class="od-set-save-error">{{ importTestResult.message }}</div>
              </div>
            </div>

            <div v-if="importInspectOk && importTestResult && importTestResult.ok" class="od-imp-step">
              <div class="od-imp-step-head"><span class="od-imp-step-num">4</span> Preview what will sync</div>
              <div class="od-imp-step-body">
                <p class="od-set-help od-imp-help-sub">
                  See exactly what a sync would bring into Event Response Manager before anything
                  is written. Rows you've already worked are never overwritten.
                </p>
                <div class="od-set-actions">
                  <b-button variant="outline-secondary" size="sm" :disabled="savingImport || previewingImport" @click="wizardPreview">
                    <i class="fa fa-search"></i>&nbsp;<span v-if="savingImport || previewingImport">Checking...</span><span v-else>Preview import</span>
                  </b-button>
                </div>
                <div v-if="importError" class="od-set-save-error">{{ importError }}</div>
                <div v-if="importRunError" class="od-set-save-error">{{ importRunError }}</div>
                <div v-if="importSummary" class="od-set-preview-box od-imp-summary">
                  <div class="od-imp-summary-head">
                    {{ importSummary.dry_run ? 'Preview' : 'Sync complete' }} &middot; {{ importSummary.sheet_rows }} rows read
                  </div>
                  <div class="od-imp-counts">
                    <span class="od-imp-c od-imp-c--new">{{ importCount('new') }} new</span>
                    <span class="od-imp-c od-imp-c--update">{{ importCount('update') }} updated</span>
                    <span class="od-imp-c">{{ importCount('unchanged') }} unchanged</span>
                    <span v-if="importCount('skipped')" class="od-imp-c od-imp-c--muted">{{ importCount('skipped') }} skipped</span>
                  </div>
                  <p v-if="importCount('skipped')" class="od-set-help od-imp-help-sub od-imp-skip-note">
                    {{ importCount('skipped') }} rows are blank and hold nothing to sync.
                  </p>
                  <div v-if="importAttentionCount" class="od-imp-attn">
                    <div class="od-imp-attn-head">{{ importAttentionCount }} {{ importAttentionCount === 1 ? 'row needs' : 'rows need' }} attention</div>
                    <div v-for="(g, gi) in importErrorGroups" :key="'g' + gi" class="od-imp-err-group">
                      <div class="od-imp-err-group-head">{{ g.count }} {{ g.count === 1 ? 'row' : 'rows' }}: {{ g.reason }}</div>
                      <div class="od-imp-err-group-items">{{ g.items }}</div>
                    </div>
                    <ul v-if="(importSummary.conflicts || []).length" class="od-imp-issues">
                      <li v-for="(c, i) in importSummary.conflicts" :key="'c' + i">Ticket {{ c.ticket || '&mdash;' }}: {{ c.reason }}</li>
                      <li v-if="importSummary.conflicts_truncated" class="od-imp-issue-more">...and {{ importSummary.conflicts_truncated }} more</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="importInspectOk && importSummary" class="od-imp-step">
              <div class="od-imp-step-head"><span class="od-imp-step-num">5</span> Turn on automatic sync</div>
              <div class="od-imp-step-body">
                <div class="od-set-access-row od-set-access-row--compact">
                  <od-toggle
                    v-model="form.import_enabled"
                    class="od-set-access-toggle"
                    :aria-label="form.import_enabled ? 'Automatic sync is on' : 'Automatic sync is off'"
                  />
                  <div class="od-set-access-text">
                    <div class="od-set-access-headline">
                      <span v-if="form.import_enabled">Syncs {{ importScheduleLabel }}</span>
                      <span v-else>Manual only</span>
                    </div>
                    <div class="od-set-access-sub">
                      <span v-if="form.import_enabled">New and updated rows flow into Event Response Manager automatically.</span>
                      <span v-else>Nothing syncs on a schedule. You can turn this on any time.</span>
                    </div>
                  </div>
                </div>
                <div v-if="form.import_enabled" class="od-huddle-when-row">
                  <div class="od-huddle-when-col">
                    <label class="od-set-label">Frequency</label>
                    <b-form-select v-model="importFreq" :options="importFreqOptions" size="sm" class="od-set-select"/>
                  </div>
                  <template v-if="importFreq === 'daily'">
                    <div class="od-huddle-when-col">
                      <label class="od-set-label">At</label>
                      <div class="od-huddle-time-picker">
                        <b-form-select v-model="importHour12" :options="huddleHourOptions" size="sm" class="od-huddle-time-sel"/>
                        <span class="od-huddle-time-colon">:00</span>
                        <b-form-select v-model="importAmPm" :options="huddleAmPmOptions" size="sm" class="od-huddle-time-sel od-huddle-time-sel--ampm"/>
                      </div>
                    </div>
                    <div class="od-huddle-when-col">
                      <label class="od-set-label">Timezone</label>
                      <b-form-select v-model="form.import_tz" :options="tzOptions" size="sm" class="od-set-select"/>
                    </div>
                  </template>
                </div>
                <div class="od-set-actions">
                  <b-button variant="primary" size="sm" :disabled="savingImport" @click="finishImportWizard">
                    <i class="fa fa-check"></i>&nbsp;<span v-if="savingImport">Saving...</span><span v-else>Finish setup</span>
                  </b-button>
                </div>
                <div v-if="importError" class="od-set-save-error">{{ importError }}</div>
              </div>
            </div>

            <div v-if="importWasConfigured" class="od-set-actions">
              <b-button variant="link" size="sm" class="od-imp-cancel-wizard" @click="cancelImportWizard">
                Cancel and keep the current connection
              </b-button>
            </div>
          </template>

          <!-- connected: compact management view -->
          <template v-else>
          <p class="od-set-help">
            Rows from your Google Sheet mirror into Event Response Manager on the schedule
            below. It's one-way and read-only, so nothing about your sheet changes,
            and entries your team works in Event Response Manager are kept.
          </p>

          <div class="od-set-preview-box od-imp-connected">
            <i class="fa fa-file-excel-o od-imp-connected-icon" aria-hidden="true"></i>
            <div class="od-imp-connected-text">
              <div class="od-imp-connected-title">{{ importTitle || 'Google Sheet' }}</div>
              <div class="od-imp-connected-sub">Tab: {{ form.import_tab }}</div>
            </div>
            <b-button variant="link" size="sm" class="od-imp-change" @click="changeImportSheet">Change sheet</b-button>
          </div>
          <p v-if="importLastRunLabel" class="od-set-help od-imp-help-sub">{{ importLastRunLabel }}</p>

          <label class="od-set-label">Automatic sync</label>
          <div class="od-set-access-row od-set-access-row--compact">
            <od-toggle
              v-model="form.import_enabled"
              class="od-set-access-toggle"
              :aria-label="form.import_enabled ? 'Automatic sync is on' : 'Automatic sync is off'"
            />
            <div class="od-set-access-text">
              <div class="od-set-access-headline">
                <span v-if="form.import_enabled">Syncs {{ importScheduleLabel }}</span>
                <span v-else>Manual only</span>
              </div>
              <div class="od-set-access-sub">
                <span v-if="form.import_enabled">New and updated rows flow into Event Response Manager automatically. You can still sync now below.</span>
                <span v-else>Nothing syncs on a schedule. Use "Sync now" below to pull the latest.</span>
              </div>
            </div>
          </div>

          <div v-if="form.import_enabled" class="od-huddle-when-row">
            <div class="od-huddle-when-col">
              <label class="od-set-label">Frequency</label>
              <b-form-select v-model="importFreq" :options="importFreqOptions" size="sm" class="od-set-select"/>
            </div>
            <template v-if="importFreq === 'daily'">
              <div class="od-huddle-when-col">
                <label class="od-set-label">At</label>
                <div class="od-huddle-time-picker">
                  <b-form-select v-model="importHour12" :options="huddleHourOptions" size="sm" class="od-huddle-time-sel"/>
                  <span class="od-huddle-time-colon">:00</span>
                  <b-form-select v-model="importAmPm" :options="huddleAmPmOptions" size="sm" class="od-huddle-time-sel od-huddle-time-sel--ampm"/>
                </div>
              </div>
              <div class="od-huddle-when-col">
                <label class="od-set-label">Timezone</label>
                <b-form-select v-model="form.import_tz" :options="tzOptions" size="sm" class="od-set-select"/>
              </div>
            </template>
          </div>

          <div class="od-set-actions">
            <div class="od-set-actions-primary">
              <span v-if="importDirty" class="od-set-unsaved-hint">Unsaved changes</span>
              <span v-else-if="importSavedAt" class="od-set-saved-hint">Saved {{ formatRelative(importSavedAt) }}</span>
              <b-button variant="primary" size="sm" :disabled="savingImport || !importDirty" @click="saveImport">
                <i class="fa fa-save"></i>&nbsp;<span v-if="savingImport">Saving...</span><span v-else>Save</span>
              </b-button>
            </div>
          </div>
          <div v-if="importError" class="od-set-save-error">{{ importError }}</div>

          <label class="od-set-label od-imp-run-label">Sync now</label>
          <p class="od-set-help od-imp-help-sub">
            Preview shows what a sync would change without writing. Sync now applies new
            and updated rows. Conflicts (a row you edited in Event Response Manager that also changed
            in the sheet) and unmapped rows are reported, never overwritten.
          </p>
          <div class="od-set-actions">
            <b-button variant="outline-secondary" size="sm" :disabled="importBusy || !importConfigured" @click="previewImport">
              <i class="fa fa-search"></i>&nbsp;<span v-if="previewingImport">Checking...</span><span v-else>Preview changes</span>
            </b-button>
            <b-button variant="primary" size="sm" :disabled="importBusy || !importConfigured" @click="runImport">
              <i class="fa fa-refresh"></i>&nbsp;<span v-if="runningImport">Syncing...</span><span v-else>Sync now</span>
            </b-button>
          </div>
          <p v-if="importDirty" class="od-set-help od-imp-help-sub">Save your changes above before syncing.</p>
          <div v-if="importRunError" class="od-set-save-error">{{ importRunError }}</div>

          <div v-if="importSummary" class="od-set-preview-box od-imp-summary">
            <div class="od-imp-summary-head">
              {{ importSummary.dry_run ? 'Preview' : 'Sync complete' }} &middot; {{ importSummary.sheet_rows }} rows read
            </div>
            <div class="od-imp-counts">
              <span class="od-imp-c od-imp-c--new">{{ importCount('new') }} new</span>
              <span class="od-imp-c od-imp-c--update">{{ importCount('update') }} updated</span>
              <span class="od-imp-c">{{ importCount('unchanged') }} unchanged</span>
              <span v-if="importCount('skipped')" class="od-imp-c od-imp-c--muted">{{ importCount('skipped') }} skipped</span>
            </div>
            <div v-if="!importSummary.dry_run && importSummary.applied" class="od-imp-applied">
              Wrote {{ importSummary.applied.created }} new and {{ importSummary.applied.updated }} updated {{ (importSummary.applied.created + importSummary.applied.updated) === 1 ? 'event' : 'events' }}.
            </div>
            <p v-if="importCount('skipped')" class="od-set-help od-imp-help-sub od-imp-skip-note">
              {{ importCount('skipped') }} rows are blank and hold nothing to sync.
            </p>
            <div v-if="importAttentionCount" class="od-imp-attn">
              <div class="od-imp-attn-head">{{ importAttentionCount }} {{ importAttentionCount === 1 ? 'row needs' : 'rows need' }} attention</div>
              <div v-for="(g, gi) in importErrorGroups" :key="'g' + gi" class="od-imp-err-group">
                <div class="od-imp-err-group-head">{{ g.count }} {{ g.count === 1 ? 'row' : 'rows' }}: {{ g.reason }}</div>
                <div class="od-imp-err-group-items">{{ g.items }}</div>
              </div>
              <ul v-if="(importSummary.conflicts || []).length" class="od-imp-issues">
                <li v-for="(c, i) in importSummary.conflicts" :key="'c' + i">Ticket {{ c.ticket || '&mdash;' }}: {{ c.reason }}</li>
                <li v-if="importSummary.conflicts_truncated" class="od-imp-issue-more">...and {{ importSummary.conflicts_truncated }} more</li>
              </ul>
            </div>
          </div>
          </template>
        </div>
      </section>

      </div>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment/moment'
// the project ships bootstrap-vue 2.0.0-rc.28, which predates BFormTags
// (added in BV 2.2.0). od_tag_input.vue is a small local replacement
// that matches the original markup's v-model + separator + validator
// shape, kept as its own SFC so the runtime-only Vue build can use the
// pre-compiled template instead of a template string.
import OdTagInput from './od_tag_input.vue'
import OdToggle from './od_toggle.vue'
import FiTabBar from './fi_tab_bar.vue'
import {
  get_ops_desk_settings,
  update_ops_desk_slug,
  update_ops_desk_access,
  check_ops_desk_slug,
  update_ops_desk_distribution,
  update_ops_desk_categories,
  get_huddle_subscribers,
  update_huddle_subscribers,
  update_ops_desk_departments,
  update_ops_desk_huddle_schedule,
  get_ops_desk_import_config,
  update_ops_desk_import_config,
  inspect_ops_desk_import,
  test_ops_desk_import,
  preview_ops_desk_import,
  run_ops_desk_import,
  get_users,
} from '../networking/apis'

// the universe of categories arrives in the /settings response under
// available_incident_categories / available_kudos_categories — keeps the
// admin picker in lock-step with the backend constants instead of asking
// the frontend to mirror them. an empty fallback is fine; loadAll always
// overwrites these before the categories tab is interactive.

// AWS cron grammar the backend matcher actually understands. anything
// outside this is "advanced" — see huddleCronUnparseable below.
const DAY_OPTIONS = [
  { value: 'SUN', label: 'Sun' },
  { value: 'MON', label: 'Mon' },
  { value: 'TUE', label: 'Tue' },
  { value: 'WED', label: 'Wed' },
  { value: 'THU', label: 'Thu' },
  { value: 'FRI', label: 'Fri' },
  { value: 'SAT', label: 'Sat' },
]

const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const h = i + 1
  return { value: String(h), text: String(h) }
})

const MINUTE_OPTIONS = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55']
  .map(m => ({ value: m, text: m }))

const AMPM_OPTIONS = [
  { value: 'am', text: 'AM' },
  { value: 'pm', text: 'PM' },
]

const TZ_OPTIONS = [
  { value: 'America/New_York', text: 'Eastern (America/New_York)' },
  { value: 'America/Chicago', text: 'Central (America/Chicago)' },
  { value: 'America/Denver', text: 'Mountain (America/Denver)' },
  { value: 'America/Phoenix', text: 'Arizona (America/Phoenix, no DST)' },
  { value: 'America/Los_Angeles', text: 'Pacific (America/Los_Angeles)' },
  { value: 'America/Anchorage', text: 'Alaska (America/Anchorage)' },
  { value: 'Pacific/Honolulu', text: 'Hawaii (Pacific/Honolulu, no DST)' },
  { value: 'UTC', text: 'UTC' },
]

export default {
  name: 'ops_desk_settings',
  components: { OdTagInput, OdToggle, FiTabBar },

  props: {
    // tab to open on mount. the ops events list passes ?settings_tab=<id> through
    // so deep links (e.g. the huddle panel's manage link) land on the right
    // tab; anything unrecognized falls back to the first tab.
    initialTab: { type: String, default: 'form' },
  },

  data() {
    return {
      loading: true,
      activeTab: 'form',

      incidentCategories: [],
      kudosCategories: [],
      // per-category event tallies from the settings payload; a missing key
      // means no events, so the row shows no count pill
      categoryUsage: {},
      tzOptions: TZ_OPTIONS,

      // huddle picker state. the form still ultimately persists a cron
      // string so the backend matcher doesn't have to learn a new shape;
      // these inputs compose into form.huddle_cron via watcher.
      huddleDayOptions: DAY_OPTIONS,
      huddleHourOptions: HOUR_OPTIONS,
      huddleMinuteOptions: MINUTE_OPTIONS,
      huddleAmPmOptions: AMPM_OPTIONS,
      huddleDays: ['SUN'],
      huddleHour12: '11',
      huddleMinute: '00',
      huddleAmPm: 'pm',
      // true when the cron string returned by the backend can't be expressed
      // by the picker (e.g. a hand-edited expression with ranges or step
      // values). picker stays usable; we just warn the user that saving
      // will overwrite the existing advanced form.
      huddleCronUnparseable: false,
      // huddle recipient management (admin); mutations apply immediately
      huddleSubscribers: [],
      huddleSubAdd: '',
      huddleSubBusy: false,
      huddleSubError: '',

      // canonical share URL for the public form. backed by the existing
      // shortener service so customers see a branded short link instead
      // of the raw web path. populated on settings GET and refreshed by
      // the slug PATCH response so a successful save lands the new URL
      // without a second round-trip.
      publicShortUrl: '',

      form: {
        slug: '',
        public_form_enabled: true,
        fi_user_ids: [],
        email_only: [],
        // department NAMES routed by the distribution; distinct from
        // form.departments (the {name, members[]} entities themselves).
        distribution_departments: [],
        enabled_incident: [],
        enabled_kudos: [],
        departments: [],
        huddle_enabled: false,
        huddle_cron: '0 23 ? * SUN *',
        huddle_tz: 'America/New_York',
        fa_brief_enabled: false,
        // FI-1964 google sheet -> ops events import
        import_spreadsheet_id: '',
        import_tab: 'Sheet1',
        import_enabled: false,
        import_tz: 'America/New_York',
      },

      // FI-1964 import tab. the daily-cron picker composes into a cron string
      // the backend matcher understands, same as the huddle picker.
      // the connect flow is a wizard (share -> link -> pick tab -> preview ->
      // schedule) until a sheet is saved; after that a compact management
      // view renders and "Change sheet" re-enters the wizard.
      importWizardActive: false,
      importWasConfigured: false, // gates the wizard's cancel affordance
      inspectingImport: false,
      importInspect: null,      // { ok, title, tabs[], message } from inspect
      importTitle: '',          // saved spreadsheet title for the connected view
      importServiceAccount: '',
      // hourly rides the dispatcher's every-hour tick (hour=* cron); daily
      // composes the picker time below. the picker keeps its value while
      // hourly is selected so switching back to daily restores it.
      importFreq: 'daily',
      importFreqOptions: [
        { value: 'hourly', text: 'Every hour' },
        { value: 'daily', text: 'Once a day' },
      ],
      importHour12: '8',
      importMinute: '00',
      importAmPm: 'am',
      savingImport: false,
      importSavedAt: null,
      importError: '',
      testingImport: false,
      importTestResult: null,   // { ok, message }
      previewingImport: false,
      runningImport: false,
      importRunError: '',
      importSummary: null,      // last preview / run summary
      importSaCopied: false,
      importLastRun: null,      // { at, status, error } from the config row

      // live tenant-level fleet assistant flag from the settings GET;
      // defaults on so the brief toggle doesn't flash disabled while
      // the page loads.
      faTenantEnabled: true,

      // tenant user candidate pool for the FI users typeahead. populated
      // lazily on the first Distribution-tab focus; cached for the page.
      tenantUserCandidates: [],
      tenantUsersLoaded: false,
      tenantUsersLoading: false,

      // department picker state. defaults come from the canonical "common
      // teams" list shared across fleets; the picker filters out anything
      // already in form.departments so the suggestion row collapses as
      // the admin clicks through.
      deptDraft: '',
      deptSuggestions: [
        'Maintenance', 'Safety', 'Dispatch', 'Customer Care',
        'Operations', 'Charter Sales', 'HR', 'Training', 'IT',
      ],

      // per-tab save state
      savingSlug: false,
      savingAccess: false,
      savingDistribution: false,
      savingCategories: false,
      savingDepartments: false,
      savingHuddle: false,
      savingFaBrief: false,

      // per-tab saved-at timestamps; ui shows "Saved 5 sec ago"
      slugSavedAt: null,
      accessSavedAt: null,
      distributionSavedAt: null,
      categoriesSavedAt: null,
      departmentsSavedAt: null,
      huddleSavedAt: null,
      faBriefSavedAt: null,

      // last successfully saved huddle schedule. the fa-brief auto-save
      // PATCHes the same row (full-row replace server-side), so it must
      // send these SAVED values, never pending picker edits.
      lastSavedHuddle: null,

      // per-tab inline save errors; a failed save renders the red box and
      // clears the saved-at hint so the page never asserts success after
      // a failure.
      accessError: '',
      distributionError: '',
      categoriesError: '',
      departmentsError: '',
      huddleError: '',
      faBriefError: '',

      // set when a departments save was blocked by a blank-named card.
      // gates the per-card "name this" hint so a mid-edit cleared name
      // doesn't nag before the admin tries to save.
      departmentsBlankAttempted: false,

      // post-load baselines for the dirty checks (json snapshots). null
      // until the first successful loadAll so a failed load can't light
      // up Save over data we never had.
      savedSnapshots: {
        distribution: null,
        categories: null,
        departments: null,
        huddle: null,
        import: null,
      },

      // slug-specific validation error (uniqueness conflict, bad shape)
      slugError: '',

      // last persisted slug from the backend — distinguishes "the value
      // in the input is unsaved" from "this is what's live".
      savedSlug: '',

      // realtime availability + short-url preview state. populated by a
      // debounced check on every slug edit so the admin sees
      // Available / Taken / Invalid as they type, plus a preview of
      // the short URL that will get created on save.
      slugCheck: {
        slug: '',
        available: null,        // true | false | null (unknown / pending)
        reason: '',             // empty | invalid_shape | taken | free | owned_by_self
        previewShortUrl: '',
      },
      slugChecking: false,
      slugCheckSeq: 0,         // last-write-wins guard for in-flight checks
      slugCheckTimer: null,

      // transient "copied!" affordance on the copy button. seconds-grained
      // so a second click doesn't re-flash the icon prematurely.
      copiedAt: null,
    }
  },

  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    tenant() {
      return this.$route.query.tenant || (this.user && this.user.tenant)
    },
    // tab bar definitions; dots mirror each tab's dirty state.
    tabDefs() {
      return [
        { id: 'form', label: 'Public form' },
        { id: 'distribution', label: 'Notifications', dot: this.distributionDirty },
        { id: 'categories', label: 'Categories', dot: this.categoriesDirty },
        { id: 'departments', label: 'Departments', dot: this.departmentsDirty },
        { id: 'huddle', label: 'Huddle', dot: this.huddleDirty },
        { id: 'import', label: 'Import', dot: this.importDirty },
      ]
    },
    // fleet assistant availability for this tenant, hydrated from the
    // settings GET. the login features map drops explicit-false flags so
    // it cannot answer this; the composite response carries the live value.
    faEnabled() {
      return this.faTenantEnabled
    },
    // a draft slug is one the user is typing but hasn't saved yet.
    // used to decide whether the preview URL is "real" (saved) or
    // "predicted" (will be created on save).
    slugIsDraft() {
      const cur = (this.form.slug || '').trim().toLowerCase()
      return !!cur && cur !== (this.savedSlug || '').toLowerCase()
    },
    publicUrlPreview() {
      // when the field matches the saved slug, show the live short URL
      // the server already created. when the field is a draft, show the
      // predicted short URL from the slug-check probe so the admin can
      // see what their save is about to create.
      if (!this.slugIsDraft) return this.publicShortUrl || ''
      return this.slugCheck.previewShortUrl || ''
    },
    // combined input state for b-form-input's :state binding.
    // null = neutral, true = green (available + valid), false = red.
    slugInputState() {
      const cur = (this.form.slug || '').trim().toLowerCase()
      if (!cur) return null
      if (this.slugError) return false
      // at rest (input matches the saved value) stay neutral — the "Saved"
      // chip is the single confirmation. validation coloring only applies
      // while the admin is actively typing a new path.
      if (!this.slugIsDraft) return null
      if (this.slugCheck.slug !== cur) return null  // pending re-check
      if (this.slugCheck.available === true) return true
      if (this.slugCheck.available === false) return false
      return null
    },
    // short banner under the input — the human-readable verdict.
    slugStatusText() {
      if (this.slugChecking) return 'Checking availability...'
      const cur = (this.form.slug || '').trim().toLowerCase()
      if (!cur || this.slugCheck.slug !== cur) return ''
      switch (this.slugCheck.reason) {
        case 'free':           return 'Available'
        // owned_by_self == resting state; stay silent so the "Saved" chip
        // is the only confirmation (no redundant status line).
        case 'owned_by_self':  return ''
        case 'taken':          return 'Already in use by another account'
        case 'invalid_shape':  return 'Lowercase letters, digits, and hyphens; 3 to 40 chars; must start with a letter'
        case 'empty':          return ''
        default:               return ''
      }
    },
    slugStatusClass() {
      if (this.slugChecking) return 'od-set-slug-status--neutral'
      if (this.slugInputState === true) return 'od-set-slug-status--ok'
      if (this.slugInputState === false) return 'od-set-slug-status--bad'
      return 'od-set-slug-status--neutral'
    },
    slugStatusIcon() {
      if (this.slugChecking) return 'fa-spinner fa-spin'
      if (this.slugInputState === true) return 'fa-check-circle'
      if (this.slugInputState === false) return 'fa-times-circle'
      return 'fa-info-circle'
    },
    slugCanSave() {
      const cur = (this.form.slug || '').trim().toLowerCase()
      if (!cur) return false
      if (this.slugChecking) return false
      // require a real edit before Save lights up — otherwise the button
      // reads as "you have unsaved work" when the input still matches
      // what's on the server. the row also switches to a "Saved" chip
      // when not draft, so this is belt-and-suspenders.
      if (!this.slugIsDraft) return false
      // block save when the slug is taken by someone else or shape-invalid.
      return this.slugCheck.slug === cur && this.slugCheck.available === true
    },
    // FI-1818 tenant user candidates pre-formatted for od-tag-input.
    // each suggestion shows username + role/name as sublabel so admins
    // recognize teammates without round-tripping to settings.
    fiUserSuggestions() {
      const candidates = Array.isArray(this.tenantUserCandidates) ? this.tenantUserCandidates : []
      return candidates.map(u => {
        const username = (u.username || '').trim()
        // user records carry first_name / last_name (not a combined name).
        const fullName = [u.first_name, u.last_name]
          .map(p => (p || '').trim()).filter(Boolean).join(' ')
        const role = (u.role || '').trim()
        return {
          value: username,
          // name leads, email is the secondary line, role rides as a chip.
          // fall back to the email as the label when no name is on file.
          label: fullName || username,
          sublabel: fullName ? username : undefined,
          chip: role ? this.readableLabel(role) : undefined,
        }
      }).filter(s => !!s.value)
    },
    // departments defined on the Departments tab, offered as a closed
    // set for the distribution. member count rides as the chip so the
    // admin can see what a pick fans out to.
    departmentSuggestions() {
      return (this.form.departments || [])
        .map(d => {
          const name = (d.name || '').trim()
          const n = (d.members || []).length
          return {
            value: name,
            label: name,
            chip: n === 1 ? '1 member' : n + ' members',
          }
        })
        .filter(s => !!s.value)
    },
    // for the email-only field we surface the same tenant users as
    // gentle suggestions (their usernames are emails for admin / dispatch
    // accounts) PLUS anything already saved as email_only — so a teammate
    // who's been hand-typed before can be re-picked from the dropdown.
    // not restrictive: an external partner's email is still valid input.
    emailOnlySuggestions() {
      const out = []
      const seen = new Set()
      for (const u of (this.tenantUserCandidates || [])) {
        const v = (u.username || '').trim()
        if (!v || seen.has(v.toLowerCase())) continue
        if (!this.emailValidator(v)) continue
        seen.add(v.toLowerCase())
        const fullName = [u.first_name, u.last_name]
          .map(p => (p || '').trim()).filter(Boolean).join(' ')
        const role = (u.role || '').trim()
        out.push({
          value: v,
          label: v,
          sublabel: fullName || undefined,
          chip: role ? this.readableLabel(role) : undefined,
        })
      }
      return out
    },
    availableDeptSuggestions() {
      const taken = new Set((this.form.departments || []).map(d => (d.name || '').toLowerCase()))
      return this.deptSuggestions.filter(d => !taken.has(d.toLowerCase()))
    },
    huddleScheduleLabel() {
      const days = this.huddleDays || []
      if (!days.length) return ''
      const tz = this.form.huddle_tz || ''
      const tzShort = tz.split('/').pop().replace(/_/g, ' ')
      const dowPluralMap = {
        SUN: 'Sundays', MON: 'Mondays', TUE: 'Tuesdays', WED: 'Wednesdays',
        THU: 'Thursdays', FRI: 'Fridays', SAT: 'Saturdays',
      }
      const dowShortMap = {
        SUN: 'Sun', MON: 'Mon', TUE: 'Tue', WED: 'Wed',
        THU: 'Thu', FRI: 'Fri', SAT: 'Sat',
      }
      const orderedDays = DAY_OPTIONS.map(d => d.value).filter(v => days.includes(v))
      const weekdaySet = ['MON', 'TUE', 'WED', 'THU', 'FRI']
      const weekendSet = ['SAT', 'SUN']
      let dowLabel
      if (orderedDays.length === 7) dowLabel = 'Every day'
      else if (orderedDays.length === 5 && weekdaySet.every(d => orderedDays.includes(d))) dowLabel = 'Weekdays'
      else if (orderedDays.length === 2 && weekendSet.every(d => orderedDays.includes(d))) dowLabel = 'Weekends'
      else if (orderedDays.length === 1) dowLabel = dowPluralMap[orderedDays[0]] || orderedDays[0]
      else dowLabel = orderedDays.map(d => dowShortMap[d] || d).join(', ')
      const minute = this.huddleMinute || '00'
      const minuteText = minute === '00' ? '' : ':' + minute
      const timeText = (this.huddleHour12 || '12') + minuteText + (this.huddleAmPm || 'pm')
      return dowLabel + ' at ' + timeText + (tzShort ? ' ' + tzShort : '')
    },
    // canonical serializations for the dirty checks. tag and checkbox
    // arrays sort first so click order doesn't read as an edit;
    // departments keep order because the cards are an ordered list.
    distributionSnapshot() {
      return JSON.stringify({
        fi: (this.form.fi_user_ids || []).slice().sort(),
        em: (this.form.email_only || []).slice().sort(),
        dep: (this.form.distribution_departments || []).slice().sort(),
      })
    },
    categoriesSnapshot() {
      return JSON.stringify({
        inc: (this.form.enabled_incident || []).slice().sort(),
        kud: (this.form.enabled_kudos || []).slice().sort(),
      })
    },
    departmentsSnapshot() {
      return JSON.stringify((this.form.departments || []).map(d => ({
        name: d.name || '',
        members: (d.members || []).slice(),
      })))
    },
    huddleSnapshot() {
      // fa_brief_enabled is deliberately absent: it auto-saves on flip
      // (saveFaBrief) and must not light the tab's dirty dot.
      return JSON.stringify({
        enabled: !!this.form.huddle_enabled,
        tz: this.form.huddle_tz,
        days: DAY_OPTIONS.map(d => d.value).filter(v => (this.huddleDays || []).includes(v)),
        hour: this.huddleHour12,
        minute: this.huddleMinute,
        ampm: this.huddleAmPm,
      })
    },
    distributionDirty() {
      return this.savedSnapshots.distribution !== null
        && this.distributionSnapshot !== this.savedSnapshots.distribution
    },
    categoriesDirty() {
      return this.savedSnapshots.categories !== null
        && this.categoriesSnapshot !== this.savedSnapshots.categories
    },
    departmentsDirty() {
      return this.savedSnapshots.departments !== null
        && this.departmentsSnapshot !== this.savedSnapshots.departments
    },
    huddleDirty() {
      // an advanced cron the picker can't represent counts as dirty: the
      // picker shows a schedule that isn't what's stored, and saving
      // replaces it (the warning box explains).
      if (this.huddleCronUnparseable) return true
      return this.savedSnapshots.huddle !== null
        && this.huddleSnapshot !== this.savedSnapshots.huddle
    },
    // FI-1964 import tab
    importSnapshot() {
      return JSON.stringify({
        sheet: (this.form.import_spreadsheet_id || '').trim(),
        tab: (this.form.import_tab || '').trim(),
        enabled: !!this.form.import_enabled,
        freq: this.importFreq,
        tz: this.form.import_tz,
        hour: this.importHour12,
        minute: this.importMinute,
        ampm: this.importAmPm,
      })
    },
    importDirty() {
      return this.savedSnapshots.import !== null
        && this.importSnapshot !== this.savedSnapshots.import
    },
    // preview / run read the SAVED config server-side, so they're only
    // available once a sheet is saved and there are no pending edits.
    importConfigured() {
      return !this.importDirty && !!(this.form.import_spreadsheet_id || '').trim()
    },
    importBusy() {
      return this.previewingImport || this.runningImport
    },
    importInspectOk() {
      return !!(this.importInspect && this.importInspect.ok)
    },
    // rows the admin has to act on: mapping/duplicate errors + conflicts.
    // rendered as its own section below the skipped note so the number can't
    // read as a caption for the skip explanation.
    importAttentionCount() {
      return this.importCount('error') + this.importCount('conflict')
    },
    // errors grouped by reason so 400 identical rows render as one line.
    // counts come from the server's exact error_reasons aggregate; the
    // itemized (capped) samples supply the ticket/row pointers, deduped so a
    // ticket that appears on 3 rows lists once (the reason carries the count).
    importErrorGroups() {
      const s = this.importSummary
      if (!s || !(s.errors || []).length) return []
      const byReason = {}
      const order = []
      for (const er of s.errors) {
        const reason = er.reason || 'error'
        if (!byReason[reason]) { byReason[reason] = []; order.push(reason) }
        const label = er.ticket ? `Ticket ${er.ticket}` : `Row ${er.row}`
        if (!byReason[reason].includes(label)) byReason[reason].push(label)
      }
      const exact = s.error_reasons || {}
      return order.map(reason => {
        const items = byReason[reason]
        const count = exact[reason] || items.length
        const shown = items.slice(0, 12)
        let label = shown.join(', ')
        // the itemized samples are capped server-side, so past the display cap
        // we only know more exist, not how many distinct tickets.
        if (items.length > shown.length || s.errors_truncated) label += ', and more'
        return { reason, count, items: label }
      })
    },
    importScheduleLabel() {
      if (this.importFreq === 'hourly') return 'every hour'
      const tz = (this.tzOptions.find(o => o.value === this.form.import_tz) || {}).text || this.form.import_tz
      const tzShort = String(tz).split(' (')[0]
      return `daily at ${this.importHour12}:00 ${this.importAmPm.toUpperCase()} ${tzShort}`
    },
    importLastRunLabel() {
      if (!this.importLastRun || !this.importLastRun.at) return ''
      const when = this.formatRelative(this.importLastRun.at)
      if (this.importLastRun.status === 'error') {
        return `Last sync ${when} didn't finish: ${this.importLastRun.error || 'unknown error'}`
      }
      return `Last synced ${when}.`
    },
    // path pieces with their trailing slash so the url box wraps after a
    // segment instead of mid-word. consecutive slashes (the scheme's //)
    // stay glued to the piece before them.
    publicUrlSegments() {
      const url = this.publicUrlPreview
      if (!url) return []
      const parts = url.split('/')
      const out = []
      for (let i = 0; i < parts.length; i++) {
        const piece = parts[i] + (i < parts.length - 1 ? '/' : '')
        if (!parts[i] && out.length) out[out.length - 1] += piece
        else if (piece) out.push(piece)
      }
      return out
    },
  },

  mounted() {
    if (this.tabDefs.some(t => t.id === this.initialTab)) this.activeTab = this.initialTab
    this.loadAll()
    // tenant user candidates power the Distribution typeahead. fire it
    // off in parallel — it's a cheap one-tenant scan and the result
    // populates the dropdown long before the admin tabs into
    // Distribution.
    this.loadTenantUsers()
  },

  watch: {
    tenant() { this.loadAll() },
    // FI-1818 access toggle persists on flip. guarded against the initial
    // hydration from loadAll() so the watcher doesn't trip a no-op save.
    'form.public_form_enabled'(val, prev) {
      if (this.loading) return
      if (val === prev) return
      this.saveAccess(val)
    },
    // fleet assistant brief is a self-contained toggle: persists on flip
    // like the access toggle, same hydration guard.
    'form.fa_brief_enabled'(val, prev) {
      if (this.loading) return
      if (val === prev) return
      this.saveFaBrief(val)
    },
    // realtime availability + preview short-url. debounce 300ms so a
    // burst of keystrokes only fires one check, and use a sequence
    // counter to drop stale responses (typing 'an' then 'and' shouldn't
    // let the slower 'an' check overwrite the result for 'and').
    'form.slug'(val) {
      const cleaned = (val || '').trim().toLowerCase()
      this.slugError = ''
      if (this.slugCheckTimer) {
        clearTimeout(this.slugCheckTimer)
        this.slugCheckTimer = null
      }
      if (!cleaned) {
        this.slugCheck = { slug: '', available: null, reason: 'empty', previewShortUrl: '' }
        this.slugChecking = false
        return
      }
      // no check needed when the value already matches the saved slug. this
      // fires on load (loadAll sets form.slug to the stored value) and when
      // the admin types their current path back; loadAll already seeded the
      // owned_by_self verdict, so hitting the endpoint would be wasted.
      if (cleaned === (this.savedSlug || '').trim().toLowerCase()) {
        this.slugCheck = {
          slug: cleaned,
          available: true,
          reason: 'owned_by_self',
          previewShortUrl: this.publicShortUrl || '',
        }
        this.slugChecking = false
        return
      }
      this.slugCheckTimer = setTimeout(() => { this.runSlugCheck(cleaned) }, 300)
    },
  },

  methods: {
    async loadAll() {
      if (!this.tenant) return
      this.loading = true
      try {
        const res = await get_ops_desk_settings(this.tenant)
        const data = (res && res.value && res.value.data) || {}
        this.publicShortUrl = data.public_short_url || ''
        const settings = data.settings || {}
        const distribution = data.distribution || {}
        const categories = data.categories || {}
        const departments = data.departments || {}
        const huddle = data.huddle_schedule || {}
        this.faTenantEnabled = data.fleet_assistant_enabled !== false
        this.incidentCategories = (data.available_incident_categories || []).slice()
        this.kudosCategories = (data.available_kudos_categories || []).slice()
        this.categoryUsage = data.category_usage || {}
        this.loadHuddleSubscribers()
        this.form.slug = settings.slug || ''
        this.savedSlug = settings.slug || ''
        // public_form_enabled defaults true (legacy tenants pre-date the
        // flag and were always accepting public submissions).
        this.form.public_form_enabled = settings.public_form_enabled !== false
        // seed the check state so the input shows "current slug" without
        // a flash of "checking…" on first render.
        if (this.savedSlug) {
          this.slugCheck = {
            slug: this.savedSlug,
            available: true,
            reason: 'owned_by_self',
            previewShortUrl: this.publicShortUrl || '',
          }
        }
        this.form.fi_user_ids = (distribution.fi_user_ids || []).map(String)
        this.form.email_only = (distribution.email_only || []).map(String)
        this.form.distribution_departments = (distribution.departments || []).map(String)
        this.form.enabled_incident = (categories.enabled_incident || []).slice()
        this.form.enabled_kudos = (categories.enabled_kudos || []).slice()
        // departments arrive as {name, members[]} entities; legacy rows may
        // still be bare strings, so coerce defensively.
        this.form.departments = (departments.departments || []).map(d => (
          typeof d === 'string'
            ? { name: d, members: [] }
            : { name: d.name, members: Array.isArray(d.members) ? d.members.slice() : [] }
        ))
        this.form.huddle_enabled = !!huddle.enabled
        this.form.huddle_cron = huddle.cron || '0 23 ? * SUN *'
        this.form.huddle_tz = huddle.tz || 'America/New_York'
        this.form.fa_brief_enabled = !!huddle.fa_brief_enabled
        this.hydrateHuddlePickerFromCron(this.form.huddle_cron)
        this.lastSavedHuddle = {
          cron: this.form.huddle_cron,
          tz: this.form.huddle_tz,
          enabled: this.form.huddle_enabled,
        }
        // FI-1964 import config loads with the rest so its dirty baseline is
        // snapshotted together with the other tabs. a failure here is soft —
        // the tab still renders with defaults.
        try {
          const ires = await get_ops_desk_import_config(this.tenant)
          const icfg = (ires && ires.value && ires.value.data) || {}
          this.importServiceAccount = icfg.service_account_email || ''
          this.form.import_spreadsheet_id = icfg.spreadsheet_id || ''
          this.form.import_tab = icfg.tab || 'Sheet1'
          this.form.import_enabled = !!icfg.enabled
          this.form.import_tz = icfg.tz || 'America/New_York'
          this.importTitle = icfg.spreadsheet_title || ''
          this.hydrateImportPickerFromCron(icfg.cron || '0 8 * * ? *')
          this.importLastRun = icfg.last_run_at
            ? { at: icfg.last_run_at, status: icfg.last_run_status, error: icfg.last_error }
            : null
          // no sheet saved yet -> guided connect wizard; otherwise the
          // compact management view (Change sheet re-enters the wizard).
          this.importWizardActive = !icfg.spreadsheet_id
          this.importWasConfigured = !!icfg.spreadsheet_id
          this.importInspect = null
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('ops_desk_settings load import config failed', e)
        }
        // fresh hydration is the new clean state for every tab
        this.snapshotBaselines()
        this.departmentsBlankAttempted = false
        this.clearSaveErrors()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings loadAll failed', e)
      } finally {
        this.loading = false
      }
    },

    // baseline the dirty-tracked tabs to the current form state. called
    // after load and per tab after each successful save.
    snapshotBaselines() {
      this.savedSnapshots = {
        distribution: this.distributionSnapshot,
        categories: this.categoriesSnapshot,
        departments: this.departmentsSnapshot,
        huddle: this.huddleSnapshot,
        import: this.importSnapshot,
      }
    },

    clearSaveErrors() {
      this.accessError = ''
      this.distributionError = ''
      this.categoriesError = ''
      this.departmentsError = ''
      this.huddleError = ''
      this.faBriefError = ''
      this.importError = ''
      this.importRunError = ''
    },

    // tenant user roster -> typeahead candidates. cached once per page
    // load. errors are soft: the field falls back to free-form entry so
    // a transient user-list failure doesn't block saving distribution.
    async loadTenantUsers() {
      if (!this.tenant || this.tenantUsersLoading) return
      this.tenantUsersLoading = true
      try {
        const res = await get_users(this.tenant)
        const data = (res && res.value && res.value.data) || {}
        const results = Array.isArray(data.results) ? data.results : []
        this.tenantUserCandidates = results
        this.tenantUsersLoaded = true
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings loadTenantUsers failed', e)
        this.tenantUsersLoaded = true
      } finally {
        this.tenantUsersLoading = false
      }
    },

    async loadHuddleSubscribers() {
      try {
        const res = await get_huddle_subscribers(this.tenant)
        this.huddleSubscribers = ((res && res.value && res.value.data) || {}).subscribers || []
      } catch (e) {
        // non-admins cannot list; the tab's admin gate keeps them out anyway
        this.huddleSubscribers = []
      }
    },
    async _patchHuddleSubscribers(payload) {
      this.huddleSubBusy = true
      this.huddleSubError = ''
      try {
        const res = await update_huddle_subscribers(this.tenant, payload)
        this.huddleSubscribers = ((res && res.value && res.value.data) || {}).subscribers || []
      } catch (e) {
        this.huddleSubError = 'Could not update recipients. Please try again.'
      } finally {
        this.huddleSubBusy = false
      }
    },
    addHuddleSubscriber() {
      const id = (this.huddleSubAdd || '').trim()
      if (!id) return
      this.huddleSubAdd = ''
      this._patchHuddleSubscribers({ add: [id] })
    },
    removeHuddleSubscriber(s) {
      this._patchHuddleSubscribers({ remove: [s.user_id] })
    },

    readableLabel(v) {
      if (!v) return ''
      return String(v).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    },

    // hover copy for the per-category usage pill: says what the number is
    // and defuses the "will disabling delete my history?" worry in place
    usageTitle(cat) {
      const n = this.categoryUsage[cat] || 0
      const noun = n === 1 ? 'event uses' : 'events use'
      return n + ' ' + noun + ' this category. Disabling only hides it from new entries; existing events keep it.'
    },

    formatRelative(ts) {
      if (!ts) return ''
      return moment(ts).fromNow()
    },

    emailValidator(tag) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((tag || '').trim())
    },

    async copyPublicUrl() {
      const url = this.publicUrlPreview
      if (!url) return
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(url)
        } else {
          // legacy fallback for older safari that gates clipboard api on a user
          // gesture only — execCommand still works inside a click handler.
          const ta = document.createElement('textarea')
          ta.value = url
          ta.style.position = 'fixed'
          ta.style.opacity = '0'
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
        this.copiedAt = Date.now()
        setTimeout(() => { this.copiedAt = null }, 1800)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings copyPublicUrl failed', e)
      }
    },

    async runSlugCheck(slug) {
      if (!slug) return
      const seq = ++this.slugCheckSeq
      this.slugChecking = true
      try {
        const res = await check_ops_desk_slug(this.tenant, slug)
        // drop responses that were superseded by a newer keystroke.
        if (seq !== this.slugCheckSeq) return
        const data = (res && res.value && res.value.data) || {}
        this.slugCheck = {
          slug: data.slug || slug,
          available: !!data.available,
          reason: data.reason || '',
          previewShortUrl: data.preview_short_url || '',
        }
      } catch (e) {
        if (seq !== this.slugCheckSeq) return
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings slug check failed', e)
        this.slugCheck = { slug, available: null, reason: '', previewShortUrl: '' }
      } finally {
        if (seq === this.slugCheckSeq) this.slugChecking = false
      }
    },

    // FI-1818 access toggle. wired to the b-model on od-toggle so the
    // switch save-on-flip — no separate Save button. catches errors so
    // a transient failure doesn't lock the toggle in an indeterminate
    // state; the loadAll on next mount will reconcile.
    async saveAccess(enabled) {
      this.savingAccess = true
      this.accessError = ''
      try {
        await update_ops_desk_access(this.tenant, enabled)
        this.accessSavedAt = new Date()
      } catch (e) {
        this.accessSavedAt = null
        this.accessError = 'Did not save. Try again.'
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings saveAccess failed', e)
      } finally {
        this.savingAccess = false
      }
    },

    async saveSlug() {
      this.slugError = ''
      this.savingSlug = true
      try {
        const cleaned = (this.form.slug || '').trim().toLowerCase()
        const res = await update_ops_desk_slug(this.tenant, cleaned)
        const data = (res && res.value && res.value.data) || {}
        // server returns the freshly generated short URL — adopt it
        // immediately so the Copy / Open affordances point at the new
        // slug without waiting for a settings reload.
        if (typeof data.public_short_url === 'string') {
          this.publicShortUrl = data.public_short_url
        }
        this.savedSlug = cleaned
        // refresh the check state so the input drops out of "draft" mode
        // and the URL preview hint disappears.
        this.slugCheck = {
          slug: cleaned,
          available: true,
          reason: 'owned_by_self',
          previewShortUrl: this.publicShortUrl,
        }
        this.slugSavedAt = new Date()
      } catch (e) {
        // backend returns a 400 with the message in error_data.error_message
        // when the slug is invalid or already in use; surface verbatim.
        const msg = e && e.response && e.response.data
          && e.response.data.error_data
          && (e.response.data.error_data.error_message || e.response.data.error_data.message)
        this.slugError = msg || 'Could not save slug.'
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings saveSlug failed', e)
      } finally {
        this.savingSlug = false
      }
    },

    async saveDistribution() {
      this.savingDistribution = true
      this.distributionError = ''
      try {
        await update_ops_desk_distribution(this.tenant, {
          fi_user_ids: this.form.fi_user_ids,
          email_only: this.form.email_only,
          departments: this.form.distribution_departments,
        })
        this.distributionSavedAt = new Date()
        this.savedSnapshots.distribution = this.distributionSnapshot
      } catch (e) {
        this.distributionSavedAt = null
        this.distributionError = 'Did not save. Try again.'
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings saveDistribution failed', e)
      } finally {
        this.savingDistribution = false
      }
    },

    async saveCategories() {
      this.savingCategories = true
      this.categoriesError = ''
      try {
        await update_ops_desk_categories(this.tenant, {
          enabled_incident: this.form.enabled_incident,
          enabled_kudos: this.form.enabled_kudos,
        })
        this.categoriesSavedAt = new Date()
        this.savedSnapshots.categories = this.categoriesSnapshot
      } catch (e) {
        this.categoriesSavedAt = null
        this.categoriesError = 'Did not save. Try again.'
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings saveCategories failed', e)
      } finally {
        this.savingCategories = false
      }
    },

    restoreDefaultCategories() {
      this.form.enabled_incident = this.incidentCategories.slice()
      this.form.enabled_kudos = this.kudosCategories.slice()
    },

    selectAllCategories(kind) {
      if (kind === 'incident') this.form.enabled_incident = this.incidentCategories.slice()
      else if (kind === 'kudos') this.form.enabled_kudos = this.kudosCategories.slice()
    },
    clearCategories(kind) {
      if (kind === 'incident') this.form.enabled_incident = []
      else if (kind === 'kudos') this.form.enabled_kudos = []
    },

    addDepartment(name) {
      const v = (name || '').trim()
      if (!v) return
      const lc = v.toLowerCase()
      // dedupe by name so a quick-add and a hand-typed entry can't collide.
      if ((this.form.departments || []).some(d => (d.name || '').toLowerCase() === lc)) return
      this.form.departments = [...(this.form.departments || []), { name: v, members: [] }]
    },
    removeDepartment(i) {
      const next = (this.form.departments || []).slice()
      next.splice(i, 1)
      this.form.departments = next
    },
    commitDeptDraft() {
      const v = this.deptDraft.trim()
      if (!v) return
      this.addDepartment(v)
      this.deptDraft = ''
    },

    async saveDepartments() {
      this.departmentsError = ''
      // block the save while any card is unnamed; the per-card hint says
      // why. silently dropping the card would lose its member list.
      if ((this.form.departments || []).some(d => !(d.name || '').trim())) {
        this.departmentsBlankAttempted = true
        return
      }
      this.savingDepartments = true
      try {
        // trim before saving; the backend also dedupes and normalizes, so
        // re-hydrate from its response to keep the UI in lock-step with
        // what was stored.
        const payload = (this.form.departments || [])
          .map(d => ({ name: (d.name || '').trim(), members: (d.members || []).slice() }))
        const res = await update_ops_desk_departments(this.tenant, payload)
        const data = (res && res.value && res.value.data) || {}
        if (Array.isArray(data.departments)) {
          this.form.departments = data.departments.map(d => ({
            name: d.name,
            members: Array.isArray(d.members) ? d.members.slice() : [],
          }))
        }
        this.departmentsSavedAt = new Date()
        this.departmentsBlankAttempted = false
        this.savedSnapshots.departments = this.departmentsSnapshot
      } catch (e) {
        this.departmentsSavedAt = null
        this.departmentsError = 'Did not save. Try again.'
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings saveDepartments failed', e)
      } finally {
        this.savingDepartments = false
      }
    },

    async saveHuddle() {
      this.savingHuddle = true
      this.huddleError = ''
      try {
        // recompose right before save so any picker tweak the watcher
        // hasn't synced yet still lands in the payload.
        const cron = this.composeHuddleCron()
        this.form.huddle_cron = cron
        await update_ops_desk_huddle_schedule(this.tenant, {
          cron: cron,
          tz: this.form.huddle_tz,
          enabled: this.form.huddle_enabled,
          fa_brief_enabled: this.form.fa_brief_enabled,
        })
        // once saved, the picker becomes the source of truth so any
        // legacy "advanced" warning clears.
        this.huddleCronUnparseable = false
        this.huddleSavedAt = new Date()
        this.savedSnapshots.huddle = this.huddleSnapshot
        this.lastSavedHuddle = {
          cron: cron,
          tz: this.form.huddle_tz,
          enabled: this.form.huddle_enabled,
        }
      } catch (e) {
        this.huddleSavedAt = null
        this.huddleError = 'Did not save. Try again.'
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings saveHuddle failed', e)
      } finally {
        this.savingHuddle = false
      }
    },

    toggleHuddleDay(day) {
      const i = this.huddleDays.indexOf(day)
      if (i >= 0) this.huddleDays.splice(i, 1)
      else this.huddleDays.push(day)
    },

    // FI-1983 fleet assistant brief persists on flip. the huddle PATCH is
    // a full-row replace, so send the last SAVED schedule values: flipping
    // the brief never commits pending picker edits, and an advanced legacy
    // cron passes through untouched (unlike the tab Save, which warns and
    // replaces it).
    async saveFaBrief(enabled) {
      const saved = this.lastSavedHuddle || {
        cron: this.form.huddle_cron,
        tz: this.form.huddle_tz,
        enabled: this.form.huddle_enabled,
      }
      this.savingFaBrief = true
      this.faBriefError = ''
      try {
        await update_ops_desk_huddle_schedule(this.tenant, {
          cron: saved.cron,
          tz: saved.tz,
          enabled: saved.enabled,
          fa_brief_enabled: enabled,
        })
        this.faBriefSavedAt = new Date()
      } catch (e) {
        this.faBriefSavedAt = null
        this.faBriefError = 'Did not save. Try again.'
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings saveFaBrief failed', e)
      } finally {
        this.savingFaBrief = false
      }
    },

    // parse the cron string the backend returned. picker handles minute,
    // hour, comma-separated day-of-week names — the same subset the
    // backend matcher supports. anything else trips the "advanced
    // expression" warning so the user knows saving will replace it.
    hydrateHuddlePickerFromCron(cron) {
      const fallback = () => {
        this.huddleCronUnparseable = !!(cron || '').trim()
        this.huddleDays = ['SUN']
        this.huddleHour12 = '11'
        this.huddleMinute = '00'
        this.huddleAmPm = 'pm'
      }
      const trimmed = (cron || '').trim()
      if (!trimmed) { fallback(); this.huddleCronUnparseable = false; return }
      const parts = trimmed.split(/\s+/)
      if (parts.length < 5) return fallback()
      const [minuteStr, hourStr, , , dowStr] = parts
      const minute = parseInt(minuteStr, 10)
      const hour = parseInt(hourStr, 10)
      if (!Number.isFinite(minute) || minute < 0 || minute > 59) return fallback()
      if (!Number.isFinite(hour) || hour < 0 || hour > 23) return fallback()
      const validDows = DAY_OPTIONS.map(d => d.value)
      const intToDow = { 1: 'SUN', 2: 'MON', 3: 'TUE', 4: 'WED', 5: 'THU', 6: 'FRI', 7: 'SAT' }
      const rawDows = (dowStr || '').split(',').map(s => s.trim().toUpperCase()).filter(Boolean)
      const dows = []
      for (const tok of rawDows) {
        if (validDows.includes(tok)) dows.push(tok)
        else if (intToDow[tok]) dows.push(intToDow[tok])
        else return fallback()
      }
      if (!dows.length) return fallback()
      this.huddleDays = Array.from(new Set(dows))
      // 24h -> 12h + AM/PM, with 12:xx handled correctly
      const ampm = hour >= 12 ? 'pm' : 'am'
      const display12 = ((hour + 11) % 12) + 1
      this.huddleHour12 = String(display12)
      // snap to the nearest 5-minute step so the select can match a value
      const snapped = Math.round(minute / 5) * 5
      this.huddleMinute = String(snapped % 60).padStart(2, '0')
      this.huddleAmPm = ampm
      this.huddleCronUnparseable = false
    },

    // compose the picker values back into AWS cron format. inverse of
    // hydrateHuddlePickerFromCron; called from saveHuddle and the
    // watcher so form.huddle_cron stays in sync for any downstream
    // consumer (e.g. the schedule label fallback).
    composeHuddleCron() {
      const dows = (this.huddleDays && this.huddleDays.length)
        ? DAY_OPTIONS.map(d => d.value).filter(v => this.huddleDays.includes(v))
        : ['SUN']
      let hour = parseInt(this.huddleHour12 || '12', 10)
      if (!Number.isFinite(hour)) hour = 12
      if (this.huddleAmPm === 'pm' && hour !== 12) hour += 12
      else if (this.huddleAmPm === 'am' && hour === 12) hour = 0
      const minute = parseInt(this.huddleMinute || '0', 10) || 0
      return `${minute} ${hour} ? * ${dows.join(',')} *`
    },

    // ---- FI-1964 sheet import ----

    // read a 400's human message out of the api error envelope (same shape
    // saveSlug reads); empty string when it isn't that shape.
    readApiError(e) {
      try {
        return e.response.data.error_data.error_message || ''
      } catch (x) {
        return ''
      }
    },

    importCount(kind) {
      return (this.importSummary && this.importSummary.counts && this.importSummary.counts[kind]) || 0
    },

    // cron the backend matcher understands. always on the hour: the
    // dispatcher runs once an hour on the minute-0 tick, so a nonzero minute
    // would never fire (the backend pins minute 0 too as a backstop).
    // hourly = hour wildcard, which matches every tick.
    composeImportCron() {
      if (this.importFreq === 'hourly') return '0 * * * ? *'
      let hour = parseInt(this.importHour12 || '8', 10)
      if (!Number.isFinite(hour)) hour = 8
      if (this.importAmPm === 'pm' && hour !== 12) hour += 12
      else if (this.importAmPm === 'am' && hour === 12) hour = 0
      return `0 ${hour} * * ? *`
    },

    // pull the frequency + hour out of a saved cron back into the picker. the
    // day/month fields are ignored: this ui only writes hourly or daily
    // schedules.
    hydrateImportPickerFromCron(cron) {
      const fallback = () => {
        this.importFreq = 'daily'
        this.importHour12 = '8'
        this.importMinute = '00'
        this.importAmPm = 'am'
      }
      const parts = (cron || '').trim().split(/\s+/)
      if (parts.length < 2) return fallback()
      if (parts[1] === '*' || parts[1] === '?') {
        // hour wildcard = hourly; the time picker keeps its defaults so
        // switching back to daily lands somewhere sensible.
        fallback()
        this.importFreq = 'hourly'
        return
      }
      const minute = parseInt(parts[0], 10)
      const hour = parseInt(parts[1], 10)
      if (!Number.isFinite(minute) || minute < 0 || minute > 59) return fallback()
      if (!Number.isFinite(hour) || hour < 0 || hour > 23) return fallback()
      this.importFreq = 'daily'
      this.importAmPm = hour >= 12 ? 'pm' : 'am'
      this.importHour12 = String(((hour + 11) % 12) + 1)
      // schedule is on-the-hour only; the stored minute is ignored on display.
      this.importMinute = '00'
    },

    async copyServiceAccount() {
      const v = this.importServiceAccount
      if (!v) return
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(v)
        }
        this.importSaCopied = true
        setTimeout(() => { this.importSaCopied = false }, 1500)
      } catch (e) {
        // clipboard blocked (older safari without a gesture); non-fatal.
      }
    },

    async saveImport() {
      this.savingImport = true
      this.importError = ''
      try {
        const res = await update_ops_desk_import_config(this.tenant, {
          spreadsheet_id: this.form.import_spreadsheet_id,
          tab: this.form.import_tab,
          cron: this.composeImportCron(),
          tz: this.form.import_tz,
          enabled: this.form.import_enabled,
        })
        const data = (res && res.value && res.value.data) || {}
        // reflect the server-normalized values (it extracts the bare id from a
        // pasted url and defaults the tab) so the saved snapshot matches.
        if (typeof data.spreadsheet_id === 'string') this.form.import_spreadsheet_id = data.spreadsheet_id
        if (data.tab) this.form.import_tab = data.tab
        if (typeof data.spreadsheet_title === 'string') this.importTitle = data.spreadsheet_title
        this.importSavedAt = new Date()
        this.savedSnapshots.import = this.importSnapshot
      } catch (e) {
        this.importSavedAt = null
        this.importError = this.readApiError(e) || 'Did not save. Try again.'
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings saveImport failed', e)
      } finally {
        this.savingImport = false
      }
    },

    // --- connect wizard ---

    resetImportInspect() {
      this.importInspect = null
      this.importTestResult = null
      this.importSummary = null
      this.importRunError = ''
    },

    async inspectImport() {
      if (!(this.form.import_spreadsheet_id || '').trim() || this.inspectingImport) return
      this.inspectingImport = true
      this.resetImportInspect()
      try {
        const res = await inspect_ops_desk_import(this.tenant, {
          spreadsheet_id: this.form.import_spreadsheet_id,
        })
        const data = (res && res.value && res.value.data) || {}
        this.importInspect = data
        if (data.ok) {
          // keep a previously chosen tab if it still exists, else preselect
          // the first tab; either way validate it right away so the admin
          // sees the columns check without another click.
          const titles = (data.tabs || []).map(t => t.title)
          if (!titles.includes(this.form.import_tab)) this.form.import_tab = titles[0] || 'Sheet1'
          this.testImport()
        }
      } catch (e) {
        this.importInspect = { ok: false, message: this.readApiError(e) || 'Could not reach the sheet. Try again.' }
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings inspectImport failed', e)
      } finally {
        this.inspectingImport = false
      }
    },

    pickImportTab(title) {
      this.form.import_tab = title
      this.importTestResult = null
      this.importSummary = null
      this.importRunError = ''
      this.testImport()
    },

    // preview reads the SAVED config server-side, so the wizard saves first
    // (with whatever the enabled toggle currently holds — off for a first
    // connect) and then dry-runs.
    async wizardPreview() {
      await this.saveImport()
      if (this.importError) return
      await this.previewImport()
    },

    async finishImportWizard() {
      await this.saveImport()
      if (this.importError) return
      this.importWizardActive = false
      this.importWasConfigured = true
      this.importInspect = null
    },

    changeImportSheet() {
      this.importWizardActive = true
      this.resetImportInspect()
    },

    cancelImportWizard() {
      // discard whatever the wizard touched and re-hydrate from the saved
      // config so the management view reflects what's actually live.
      this.importWizardActive = false
      this.resetImportInspect()
      this.loadAll()
    },

    async testImport() {
      this.testingImport = true
      this.importTestResult = null
      try {
        const res = await test_ops_desk_import(this.tenant, {
          spreadsheet_id: this.form.import_spreadsheet_id,
          tab: this.form.import_tab,
        })
        const data = (res && res.value && res.value.data) || {}
        this.importTestResult = {
          ok: !!data.ok,
          message: data.message || (data.ok ? 'Connected.' : 'Could not read the sheet.'),
        }
      } catch (e) {
        this.importTestResult = { ok: false, message: this.readApiError(e) || 'Could not reach the sheet. Try again.' }
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings testImport failed', e)
      } finally {
        this.testingImport = false
      }
    },

    async previewImport() {
      this.previewingImport = true
      this.importRunError = ''
      try {
        const res = await preview_ops_desk_import(this.tenant)
        this.importSummary = (res && res.value && res.value.data) || null
      } catch (e) {
        this.importRunError = this.readApiError(e) || 'Preview failed. Try again.'
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings previewImport failed', e)
      } finally {
        this.previewingImport = false
      }
    },

    async runImport() {
      this.runningImport = true
      this.importRunError = ''
      try {
        const res = await run_ops_desk_import(this.tenant, false)
        this.importSummary = (res && res.value && res.value.data) || null
        if (this.importSummary && !this.importSummary.dry_run) {
          this.importLastRun = { at: this.importSummary.ran_at, status: 'ok', error: '' }
        }
      } catch (e) {
        this.importRunError = this.readApiError(e) || 'Sync failed. Try again.'
        // eslint-disable-next-line no-console
        console.warn('ops_desk_settings runImport failed', e)
      } finally {
        this.runningImport = false
      }
    },
  },
}
</script>

<style scoped>
/* hosted in the settings modal: the modal body owns width + padding. the
   app-level #app rule sets text-align: center, which cascades into every
   label / help / caption inside the settings page; force a left baseline
   here so the tabs, form labels, checkboxes, and helper copy align flush. */
.od-set-shell {
  text-align: left;
}

.od-set-loading { text-align: center; padding: 32px; color: #6b7280; font-size: 13.5px; }

/* tab bar itself is fi_tab_bar.vue (FI-1983); its own class names mean
   navbar.vue's unscoped global .nav-tabs rules can't reach it, so the old
   !important wall is gone. */

/* content surface so the body groups with the tab bar instead of floating
   on the page background. white card hangs off the bar's bottom border. */
.od-set-tabs-content {
  background: #ffffff;
  border: 1px solid #e1e8f0;
  border-top: none;
  border-radius: 0 0 6px 6px;
  padding: 4px 0;
}
.od-set-section { padding: 18px 20px 16px 20px; }

/* section headers inside a tab (huddle uses these); new feature areas
   (insights, stats) slot in as additional sections. */
.od-set-sect-head {
  margin: 24px 0 8px 0;
  font-size: 13px;
  font-weight: 700;
  color: #1d1d1f;
  letter-spacing: -0.1px;
}

/* help-text: drop the chip-style container, just a quiet caption above
   the first field. less form-builder, more product. */
.od-set-help {
  font-size: 13px;
  color: #6b7280;
  margin: 0 0 16px 0;
  line-height: 1.5;
  background: none;
  border: none;
  padding: 0;
}
.od-set-label {
  display: block; margin: 12px 0 4px 0;
  font-size: 12px; font-weight: 700; color: #4b5563;
  text-transform: uppercase; letter-spacing: 0.04em;
}
.od-set-label--small { font-size: 11px; margin-top: 16px; }
.od-set-input, .od-set-select { max-width: 380px; }

/* inline slug row: input + Save button side-by-side. mobile collapses
   the button under the input via flex-wrap. */
.od-set-slug-row {
  display: flex;
  align-items: stretch;
  gap: 8px;
  max-width: 480px;
  flex-wrap: wrap;
}
.od-set-slug-input { flex: 1 1 260px; min-width: 200px; }
.od-set-slug-save-btn { flex: 0 0 auto; align-self: stretch; }

.od-set-slug-saved-chip {
  align-self: stretch;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0 12px;
  background: #e7f8ed;
  color: #166534;
  border: 1px solid #b5e3c5;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
}
.od-set-slug-saved-chip .fa { font-size: 11px; }

/* access row layout. toggle + headline/sub stack so the explanation
   reads as the toggle's caption rather than a separate block. */
.od-set-access-row {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 12px 14px;
  border: 1px solid #e1e8f0;
  border-radius: 6px;
  background: #f8fafc;
  max-width: 640px;
  margin-top: 4px;
}
.od-set-access-toggle { flex-shrink: 0; margin-top: 2px; }
.od-set-access-text { flex: 1; min-width: 0; }
.od-set-access-headline { font-size: 13.5px; font-weight: 600; color: #1d1d1f; }
.od-set-access-sub { font-size: 12.5px; color: #6b7280; margin-top: 3px; line-height: 1.45; }
/* compact variant: same surface, tighter padding for inline use in the
   huddle tab where vertical breathing room is already supplied by the
   sections above and below. */
.od-set-access-row--compact { padding: 10px 12px; }

/* status banner directly under the input: green / red / neutral
   depending on the availability verdict. */
.od-set-slug-status {
  margin-top: 6px;
  font-size: 12.5px;
  line-height: 1.4;
  display: flex; align-items: center; gap: 2px;
}
.od-set-slug-status--ok { color: #166534; }
.od-set-slug-status--bad { color: #b91c1c; }
.od-set-slug-status--neutral { color: #6b7280; }

.od-set-url-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #6b7280;
  font-style: italic;
}
.od-set-tags { max-width: 640px; }
.od-set-hint { font-size: 12px; color: #6b7280; margin-top: 4px; }
.od-set-error { font-size: 12.5px; color: #b91c1c; margin-top: 4px; }

/* inline failure box under a tab's save row */
.od-set-save-error {
  margin-top: 10px;
  background: #fbe3e5;
  border: 1px solid #f5c0c4;
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 13px;
  color: #b91c1c;
  max-width: 640px;
}

.od-set-url-box {
  background: #f3f4f6; border: 1px dashed #c8d4df; border-radius: 6px;
  padding: 10px 12px; max-width: 640px;
  display: flex; align-items: center; gap: 12px;
}
/* size the URL to match the form path input (16px) so the two visually
   anchor each other as the "this is what you typed → this is the URL
   it produced" pair. browser default shrinks <code> to ~87.5% of the
   parent, so set it explicitly. */
.od-set-url-box code {
  color: var(--fi-ops-accent-strong);
  flex: 1 1 auto;
  min-width: 0;
  font-size: 16px;
  line-height: 1.3;
}
/* each path piece wraps as a unit; break-all inside is the fallback for
   a single piece wider than the box */
.od-set-url-seg {
  display: inline-block;
  max-width: 100%;
  word-break: break-all;
}
.od-set-url-actions {
  display: flex; gap: 6px; flex-shrink: 0; align-items: center;
}
/* small steelblue ghost buttons that sit cleanly inside the dashed URL
   box. matches the day-chip / filter-chip palette used elsewhere on the
   Ops Desk surface: white fill, light gray border by default, steelblue
   border + tinted background on hover. */
.od-set-url-btn {
  background: #fff !important;
  border: 1px solid #c8d4df !important;
  border-radius: 4px !important;
  padding: 4px 10px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  color: var(--fi-ops-eyebrow) !important;
  display: inline-flex !important;
  align-items: center;
  gap: 4px;
  text-decoration: none !important;
  transition: background .12s, color .12s, border-color .12s;
  line-height: 1.4;
}
.od-set-url-btn:hover,
.od-set-url-btn:focus {
  background: #eef4fb !important;
  border-color: var(--fi-rail-accent) !important;
  color: var(--fi-ops-accent-strong) !important;
  box-shadow: none !important;
}
.od-set-url-btn .fa { font-size: 11px; }

/* neutral schedule information, so the blue trio rather than success green */
.od-set-preview-box {
  background: #e7f1ff; border: 1px solid #bcd8fb; border-radius: 6px;
  padding: 8px 12px; font-size: 13px; color: #0b3d91;
  max-width: 380px;
}

/* one save-row pattern for every tab: optional secondary action (e.g.
   Restore defaults) parks on the left, the hint + Save group floats
   right via the primary group's auto margin. */
.od-set-actions {
  display: flex; align-items: center; gap: 10px;
  margin-top: 20px; padding-top: 14px;
  border-top: 1px solid #eef0f3;
}
.od-set-action-secondary {
  padding-left: 0 !important; color: var(--fi-ops-eyebrow) !important;
  text-decoration: none !important; font-size: 13px;
}
.od-set-action-secondary:hover { color: var(--fi-ops-accent-strong) !important; }
.od-set-actions-primary {
  display: flex; align-items: center; gap: 10px;
  margin-left: auto;
}
.od-set-saved-hint { font-size: 12px; color: #166534; }
.od-set-unsaved-hint { font-size: 12px; color: #6b7280; }

/* categories grid */
.od-set-cats-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
  max-width: 640px;
}
.od-set-cats-col {
  background: #f8fafc; border: 1px solid #e1e8f0; border-radius: 6px; padding: 12px;
}
.od-set-cats-header {
  font-size: 12px; font-weight: 700; color: var(--fi-ops-accent-strong);
  text-transform: uppercase; letter-spacing: 0.04em;
  border-bottom: 1px solid #d6dde6; padding-bottom: 6px; margin-bottom: 6px;
  display: flex; align-items: center; justify-content: space-between;
  gap: 8px;
}
.od-set-cats-bulk {
  text-transform: none;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0;
  color: #6b7280;
  display: inline-flex;
  align-items: center;
  gap: 2px;
}
.od-set-cats-bulk-btn {
  background: transparent; border: none; padding: 0 4px;
  color: var(--fi-ops-accent-strong); cursor: pointer;
  font: inherit;
}
.od-set-cats-bulk-btn:hover { color: var(--fi-rail-accent); text-decoration: underline; }
.od-set-cats-bulk-sep { color: #d6dde6; padding: 0 2px; }
.od-set-cat-row { font-size: 13px; padding: 3px 0; }
/* usage pill: quiet tally beside categories that have events, so an admin
   sees a category is live before unchecking it. muted on purpose -- it is
   context, not a call to action. */
.od-set-cat-count {
  display: inline-block;
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 999px;
  background: #e8edf3;
  color: #5b6572;
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
  vertical-align: 1px;
  cursor: default;
}

/* huddle recipients: compact rows, immediate-apply remove, inline add */
.od-set-sub-list { max-width: 480px; margin-bottom: 8px; }
.od-set-sub-row {
  display: flex; align-items: center; gap: 8px;
  padding: 4px 2px; font-size: 13px;
  border-bottom: 1px solid #edf1f6;
}
.od-set-sub-user { color: #6b7a8c; font-size: 12px; }
.od-set-sub-remove {
  margin-left: auto; border: none; background: transparent;
  color: #9aa4b1; cursor: pointer; padding: 2px 6px;
}
.od-set-sub-remove:hover { color: #c0392b; }
.od-set-sub-empty { color: #6b7a8c; font-size: 13px; padding: 4px 2px; }
.od-set-sub-add { display: flex; gap: 8px; max-width: 480px; }
.od-set-sub-input { max-width: 320px; }
/* bootstrap's default #007bff check fill matches classic's action color by
   coincidence and is wrong in the other themes; key it to the action token.
   bv renders the control internals without this component's scope attribute,
   hence ::v-deep. */
.od-set-cat-check ::v-deep .custom-control-input:checked ~ .custom-control-label::before {
  background-color: var(--fi-action-primary);
  border-color: var(--fi-action-primary);
}

/* advisory for an empty incident set; the save still goes through */
.od-set-cats-warning {
  margin-top: 12px;
  background: #fff8ef;
  border: 1px solid #f0d9b5;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 12.5px;
  color: #7a5400;
  line-height: 1.5;
  max-width: 640px;
}

/* department entities: a stack of cards, each with a name field, a remove
   control, and a member tag-input. quick-add chips + a custom-add row sit
   below the list. */
.od-dept-list {
  display: flex; flex-direction: column; gap: 10px;
  max-width: 640px;
  margin-bottom: 14px;
}
.od-dept-card {
  border: 1px solid #e1e8f0;
  border-radius: 6px;
  background: #f8fafc;
  padding: 12px 14px;
}
.od-dept-card-head {
  display: flex; align-items: center; gap: 8px;
  margin-bottom: 4px;
}
.od-dept-name-input { flex: 1; }
.od-dept-name-input ::v-deep .form-control {
  font-weight: 600;
}
.od-dept-remove {
  flex: 0 0 auto;
  background: #fff;
  border: 1px solid #e3c9cc;
  width: 30px; height: 30px;
  border-radius: 4px;
  color: #b45a5a;
  cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  transition: background .12s, color .12s, border-color .12s;
}
.od-dept-remove:hover { background: #fbeaea; border-color: #d99; color: #a23030; }
.od-dept-name-error {
  margin: 2px 0 8px 0;
  background: #fbe3e5;
  border: 1px solid #f5c0c4;
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 13px;
  color: #b91c1c;
}
.od-dept-member-empty {
  font-size: 12px; color: #6b7280;
  margin-top: 4px;
}
.od-dept-empty {
  font-size: 12.5px; color: #6b7280;
  padding: 12px;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
  max-width: 640px;
  margin-bottom: 14px;
}
.od-dept-suggest-row {
  display: flex; flex-wrap: wrap; gap: 6px;
  max-width: 640px;
}
.od-dept-chip {
  display: inline-flex; align-items: center; gap: 4px;
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 12.5px; font-weight: 600;
  line-height: 1.4;
  border: 1px solid;
  transition: background .12s, border-color .12s, color .12s;
}
.od-dept-chip--suggest {
  background: #f8fafc; color: #4b5563; border-color: #d6dde6;
  cursor: pointer;
}
.od-dept-chip--suggest:hover {
  background: #eef4fb; border-color: #b8c8da;
}
.od-dept-chip--suggest .fa { font-size: 10px; opacity: 0.7; }

.od-dept-custom-row {
  display: flex; gap: 8px;
  max-width: 480px;
  align-items: stretch;
}
.od-dept-custom-input { flex: 1; }

/* huddle picker: day chips + time picker. compact, mobile-friendly. */
.od-huddle-day-chips {
  display: flex; flex-wrap: wrap; gap: 6px;
  margin: 4px 0 4px 0;
}
.od-huddle-day-chip {
  background: #f8fafc; border: 1px solid #d6dde6; border-radius: 999px;
  padding: 4px 12px; font-size: 12.5px; font-weight: 600; color: #4b5563;
  cursor: pointer; transition: background .12s, border-color .12s, color .12s;
  min-width: 48px;
}
.od-huddle-day-chip:hover { background: #eef4fb; border-color: #b8c8da; }
.od-huddle-day-chip:focus-visible {
  outline: 2px solid var(--fi-ops-accent);
  outline-offset: 2px;
}
.od-huddle-day-chip--active {
  background: var(--fi-ops-accent); border-color: var(--fi-ops-accent); color: #fff;
}
.od-huddle-day-chip--active:hover { background: var(--fi-ops-accent-strong); border-color: var(--fi-ops-accent-strong); }

.od-huddle-when-row {
  display: flex; gap: 24px; flex-wrap: wrap;
  margin-top: 4px;
}
.od-huddle-when-col { flex: 0 0 auto; min-width: 220px; }
.od-huddle-time-picker {
  display: flex; align-items: center; gap: 6px;
  max-width: 240px;
}
.od-huddle-time-sel { flex: 0 0 auto; width: auto !important; min-width: 64px; }
.od-huddle-time-sel--ampm { min-width: 70px; }
.od-huddle-time-colon { font-weight: 700; color: #4b5563; padding: 0 2px; }

.od-huddle-advanced-warning {
  margin-top: 12px;
  background: #fff8ef; border: 1px solid #f0d9b5; border-radius: 6px;
  padding: 8px 12px; font-size: 12.5px; color: #7a5400;
  line-height: 1.5;
}
.od-huddle-advanced-warning code {
  color: #7a5400; background: rgba(0,0,0,0.05); padding: 0 4px; border-radius: 3px;
}

/* FI-1964 import tab. colors come from themed --fi-* tokens (or token text
   colors) so the tab reads correctly in Classic, Horizon, and Midnight
   without a per-theme override. */
.od-imp-sa {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
}
.od-imp-sa-email {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px; overflow-wrap: anywhere; color: var(--fi-text);
}
.od-imp-copy { padding: 0 6px; flex: 0 0 auto; }
.od-imp-help-sub { margin-top: 4px; }
/* the status/count inks in this import panel are mixed toward --fi-text rather
   than used raw. midnight.css has no od-imp rules at all, so these sit
   straight on --fi-surface, where raw --fi-success is 3.03:1, --fi-danger
   2.40:1 and --fi-ops-accent 3.41:1 -- all under the 4.5:1 floor these 12-13px
   labels need. the mix deepens them on the light themes and lightens them on
   midnight, clearing both (success 6.71:1, danger 5.46:1, accent 6.82:1).
   same trick as .ad-failure-reason. */
.od-imp-test-ok { color: color-mix(in srgb, var(--fi-success) 45%, var(--fi-text)); }
.od-imp-run-label { margin-top: 24px; }
.od-imp-summary { margin-top: 12px; }
.od-imp-summary-head { font-weight: 600; color: var(--fi-text); margin-bottom: 6px; }
.od-imp-counts {
  display: flex; flex-wrap: wrap; gap: 12px; font-size: 12px; margin-bottom: 6px;
}
.od-imp-c { color: var(--fi-text-muted); }
.od-imp-c--new { color: color-mix(in srgb, var(--fi-success) 45%, var(--fi-text)); font-weight: 600; }
.od-imp-c--update { color: color-mix(in srgb, var(--fi-ops-accent) 45%, var(--fi-text)); font-weight: 600; }
.od-imp-applied { font-size: 12px; color: var(--fi-text-muted); margin-bottom: 6px; }
.od-imp-issues {
  margin: 6px 0 0; padding-left: 18px; font-size: 12px; color: var(--fi-text-muted);
}
.od-imp-issues li { margin-bottom: 2px; }
.od-imp-issue-more { font-style: italic; }

/* connect wizard: numbered steps. steps appear progressively (v-if), so
   there's no connector rail, the circles alone carry the sequence. */
.od-imp-step { position: relative; padding-left: 34px; margin-top: 18px; }
.od-imp-step-head {
  font-weight: 600; color: var(--fi-text); font-size: 13px; margin-bottom: 6px;
}
.od-imp-step-num {
  position: absolute; left: 0; top: -2px; width: 24px; height: 24px;
  border-radius: 50%; background: var(--fi-ops-accent); color: #fff;
  font-size: 12px; font-weight: 700; display: inline-flex;
  align-items: center; justify-content: center;
}
.od-imp-step-body { min-width: 0; }
.od-imp-connect-row { display: flex; gap: 8px; align-items: flex-start; }
.od-imp-connect-row .od-set-input { flex: 1 1 auto; }
.od-imp-connect-row .btn { flex: 0 0 auto; }
.od-imp-tab-list { display: flex; flex-direction: column; gap: 6px; max-width: 420px; }
.od-imp-tab-opt {
  display: flex; align-items: center; gap: 8px; margin: 0; padding: 7px 10px;
  border: 1px solid var(--fi-line); border-radius: 6px; cursor: pointer;
  font-size: 13px; color: var(--fi-text); background: var(--fi-surface);
}
.od-imp-tab-opt:hover { border-color: var(--fi-ops-accent); }
.od-imp-tab-opt--active {
  border-color: var(--fi-ops-accent);
  box-shadow: 0 0 0 1px var(--fi-ops-accent);
}
.od-imp-tab-opt .fa { color: var(--fi-text-muted); }
.od-imp-tab-radio { position: absolute; opacity: 0; pointer-events: none; }
.od-imp-tab-name { overflow-wrap: anywhere; }
.od-imp-cancel-wizard { padding-left: 0; font-size: 12px; }
.od-imp-c--muted { color: var(--fi-text-muted); }
.od-imp-skip-note { margin-bottom: 6px; }
.od-imp-attn {
  margin-top: 10px; padding-top: 8px; border-top: 1px solid var(--fi-line);
}
.od-imp-attn-head { font-weight: 600; font-size: 13px; color: color-mix(in srgb, var(--fi-danger) 45%, var(--fi-text)); }
.od-imp-err-group { margin-top: 6px; font-size: 12px; }
.od-imp-err-group-head { font-weight: 600; color: color-mix(in srgb, var(--fi-danger) 45%, var(--fi-text)); }
.od-imp-err-group-items { color: var(--fi-text-muted); overflow-wrap: anywhere; }
.od-imp-connected {
  display: flex; align-items: center; gap: 10px;
}
/* the glyph is 20px so 3:1 applies and raw success technically cleared it at
   3.03:1, but it is mixed like its siblings above so the panel's success ink
   stays one colour rather than two. */
.od-imp-connected-icon { font-size: 20px; color: color-mix(in srgb, var(--fi-success) 45%, var(--fi-text)); flex: 0 0 auto; }
.od-imp-connected-text { min-width: 0; flex: 1 1 auto; }
.od-imp-connected-title { font-weight: 600; color: var(--fi-text); overflow-wrap: anywhere; }
.od-imp-connected-sub { font-size: 12px; color: var(--fi-text-muted); }
.od-imp-change { flex: 0 0 auto; padding: 0 6px; font-size: 12px; }

/* family focus standard for the custom (non-bootstrap) controls, matching
   the list page's treatment. the sheet-tab option is a label wrapping a
   visually hidden radio, so its ring keys off the radio's focus state. */
.od-set-cats-bulk-btn:focus-visible,
.od-dept-remove:focus-visible,
.od-dept-chip--suggest:focus-visible {
  outline: 2px solid var(--fi-ops-accent);
  outline-offset: 2px;
}
.od-imp-tab-opt:has(.od-imp-tab-radio:focus-visible) {
  outline: 2px solid var(--fi-ops-accent);
  outline-offset: 2px;
}

@media (max-width: 600px) {
  .od-set-cats-grid { grid-template-columns: 1fr; }
  .od-set-input, .od-set-select, .od-set-tags, .od-set-url-box, .od-set-preview-box { max-width: 100%; }
}
</style>
